import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Palette, ShieldCheck, SlidersHorizontal, Save, Upload, X, LayoutDashboard, Newspaper, Plus, Trash2, Pencil, Mail, Eye, Pin, KeyRound, GitPullRequest, Users, ScrollText, AppWindow, Sparkles, FileText, Loader2, BookOpen, Download, Bell, Clock, Plug, HelpCircle, LogOut, AlertTriangle, ShieldOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { useSubmenuOrder } from "@/lib/useSubmenuOrder";
import { DataSyncOperatorsCard } from "@/components/DataSyncOperatorsCard";
import { PageHeader } from "@/components/PageHeader";
import { PartnerBrandingForm, brandingFormOf } from "@/components/PartnerBrandingForm";
import { TopBarPreview } from "@/components/TopBarPreview";
import Permissions from "@/pages/Permissions";
import Developer from "@/pages/Developer";
import Requests from "@/pages/Requests";
import UsersPage from "@/pages/Users";
import AppsPage from "@/pages/Apps";
import AppAudiencesCard from "@/pages/settings/AppAudiencesCard";
import AppAudienceGrid from "@/pages/settings/AppAudienceGrid";
import AppAudienceChangesCard from "@/pages/settings/AppAudienceChangesCard";
import { InternalIpPanel } from "@/components/InternalIpPanel";
import { SecurityStatusCard } from "@/components/SecurityStatusCard";
import { ChangelogResetCard } from "@/components/ChangelogResetCard";
import { OAuth2ClientsAdmin } from "@/components/OAuth2ClientsAdmin";
import { DbConnectionsAdmin } from "@/components/DbConnectionsAdmin";
import { ApiConnectionsAdmin } from "@/components/ApiConnectionsAdmin";
import { JobBoardSandboxToggle } from "@/components/JobBoardSandboxToggle";
import { EmailKillSwitch } from "@/components/EmailKillSwitch";
import { JobBoardRejectionCopyEditor } from "@/components/JobBoardRejectionCopyEditor";
import { CalendlyConnectPill } from "@/pages/ai_outbound/CalendlyConnectPill";
import { HelpArticlesAdmin } from "@/pages/settings/HelpArticlesAdmin";
import { HelpdeskDepartmentsAdmin, HelpdeskPrioritiesAdmin, HelpdeskTiersAdmin, HelpdeskSlaPoliciesAdmin, HelpdeskBusinessHoursAdmin, HelpdeskDeletedAdmin, HelpdeskAutomationAdmin } from "@/pages/settings/HelpdeskAdmin";
import { VendorBrandsAdmin } from "@/pages/settings/VendorBrands";
import KBImportReview from "@/pages/KBImportReview";
import KBHelpjuicePreview from "@/pages/KBHelpjuicePreview";
import KBSettings from "@/pages/KBSettings";
import { KbIntercomImportPanel } from "@/components/KbIntercomImportPanel";
import KbArticlesPanel from "@/pages/settings/KbArticlesPanel";
import UCCDRSettings from "@/pages/settings/UCCDRSettings";
import KbImagesPanel from "@/pages/settings/KbImagesPanel";
import { DashboardHeaderEditor } from "@/components/DashboardHeaderEditor";
import { CelebrationsCard } from "@/pages/settings/CelebrationsCard";
import { EmailTemplatesCard } from "@/pages/settings/EmailTemplatesCard";
import { PingdomBannerMapping } from "@/components/PingdomBannerMapping";
import { EscalationTableEditor } from "@/components/EscalationTableEditor";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/RichTextEditor";
import DOMPurify from "dompurify";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TimeTrackingReset } from "@/components/TimeTrackingReset";
import MsTeamsPresenceCard from "@/components/MsTeamsPresenceCard";
import { CloudTrailAlertsCard } from "@/components/CloudTrailAlertsCard";

// Equal-size, equally-spaced tab pill for the Settings navigation grid.
const PILL = "flex h-9 w-full items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow";

// Iter 90e — RichTextEditor operates on innerHTML, so pre-rich-text
// settings (plain strings with \n separators) render as one flat line
// when passed in raw. Detect legacy plain text (no `<`) and wrap it in
// paragraphs so the editor shows what the admin originally typed.
// Iter 120 — hour options for the nightly maintenance window selects.
const HOURS = Array.from({ length: 24 }, (_, v) => ({
  v,
  label: `${v % 12 === 0 ? 12 : v % 12}:00 ${v < 12 ? "AM" : "PM"}`,
}));

const escapeHtml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;")
                            .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const plaintextToHtml = (raw) => {
  const s = (raw || "").trim();
  if (!s) return "";
  if (/</.test(s)) return raw;                          // already HTML
  return s.split(/\n{2,}/)
          .map((para) => `<p>${escapeHtml(para).replace(/\n/g, "<br>")}</p>`)
          .join("");
};

// Iter 90e — Starter template matching the finalized escalation layout
// (label · hours · contact info). Admins tweak numbers/hours in the
// editor; the styling is applied by Dashboard's `.prose` classes.
const ESCALATION_TABLE_TEMPLATE = `\
<p>Reach out to the following contacts for technical support.</p>
<table>
  <tbody>
    <tr>
      <td><strong>Technical Support</strong></td>
      <td>Monday - Friday<br>6:30 am - 5:30 pm MST/MDT</td>
      <td><a href="mailto:support@unitedcloud.ca">support@unitedcloud.ca</a><br><a href="tel:+18006933891">(800) 693-3891</a></td>
    </tr>
    <tr>
      <td><strong>Executive Escalation</strong></td>
      <td><strong>24/7/365</strong></td>
      <td><a href="tel:+14036711306">(403) 671-1306</a> (Keith's Number)</td>
    </tr>
    <tr>
      <td><strong>Emergency Service</strong></td>
      <td><strong>24/7/365</strong><br>(After hours charges may apply at $85/hour)</td>
      <td><a href="tel:+15875737333">(587) 573-7333</a></td>
    </tr>
    <tr>
      <td><strong>Non-Technical Support</strong></td>
      <td>Monday - Friday<br>8:00 am - 4:30 pm MST/MDT</td>
      <td><a href="mailto:partners@unitedcloud.ca">partners@unitedcloud.ca</a><br><a href="tel:+18774329177">(877) 432-9177</a></td>
    </tr>
  </tbody>
</table>`;
import { toast } from "sonner";
import { Check, Sparkles as MegaphoneIcon, Wrench, ShieldAlert, Server, Search, LifeBuoy, Activity, PhoneCall, Library, GitCompareArrows, DownloadCloud, Image as ImageIcon, Receipt, Siren } from "lucide-react";
import E911Settings from "@/pages/settings/E911Settings";
import SftpDestinationsPanel from "@/pages/settings/SftpDestinationsPanel";
import S3StoragePanel from "@/pages/settings/S3StoragePanel";
import DefaultOrgPanel from "@/pages/settings/DefaultOrgPanel";
import { HardDriveUpload, Cloud, Building2 } from "lucide-react";
import { ScheduledJobsPanel } from "@/components/settings/scheduled-jobs/ScheduledJobsPanel";
import { AdminAlertRecipientsCard } from "@/components/settings/admin-alerts/AdminAlertRecipientsCard";

// Iter 77: single source of truth for every settings tab. `gate` returns
// false when the current user lacks the required permission and the tab
// is hidden.
const TAB_META = {
  // Iter 244 — a partner viewer edits their OWN white-label branding and
  // escalation copy on these two tabs; platform settings stay staff-only.
  branding: { label: "Branding",      icon: Palette , gate: ({ has, isPartnerViewer, isPartnerAdmin }) => isPartnerViewer ? isPartnerAdmin : has("settings_branding.view") },
  dashboard: { label: "Dashboard Header", icon: LayoutDashboard , gate: ({ has }) => has("settings_dashboard.view") },
  "system-status": { label: "System Status", icon: Activity, gate: ({ has }) => has("settings_system_status.view") },
  escalation: { label: "Escalation Board", icon: PhoneCall, gate: ({ has, isPartnerViewer, isPartnerAdmin }) => isPartnerViewer ? isPartnerAdmin : has("settings_escalation.view") },
  maintenance: { label: "Maintenance Banner", icon: Wrench, gate: ({ has }) => has("settings_maintenance.view") },
  news: { label: "Partner News",  icon: Newspaper , gate: ({ has }) => has("settings_news.view") },
  platform: { label: "Platform",      icon: SlidersHorizontal , gate: ({ has }) => has("settings_platform.view") },
  "db-connections": { label: "DB Connections",  icon: Server, gate: ({ has }) => has("settings_db_connections.view") },
  "api-connections": { label: "API Connections", icon: Plug, gate: ({ has }) => has("settings_api_connections.view") },
  "file-storage": { label: "File Storage", icon: Cloud, gate: ({ has }) => has("settings_platform.view") },
  sftp: { label: "SFTP destinations", icon: HardDriveUpload, gate: ({ has }) => has("settings_sftp.view") },
  "uc-cdr": { label: "UC CDR", icon: Receipt, gate: ({ has }) => has("uc_cdr.edit") && has("settings_uc_cdr.view") },
  e911: { label: "Emergency Numbers", icon: Siren, gate: ({ has }) => has("e911.edit") && has("settings_e911.view") },
  notifications: { label: "Notifications", icon: Bell , gate: ({ has }) => has("settings_notifications.view") },
  integrations: { label: "My Integrations", icon: Plug , gate: ({ has }) => has("settings_integrations.view") },
  security: { label: "Security",      icon: ShieldCheck , gate: ({ has }) => has("settings_security.view") },
  "vendor-brands": { label: "Vendor Brands", icon: ShieldOff, gate: ({ has }) => has("settings_vendor_brands.view") },
  "default-org": { label: "Default Organization", icon: Building2, gate: ({ has }) => has("settings_default_org.view") },
  staff: { label: "Staff",         icon: Users, gate: ({ has }) => has("settings_staff.view") },
  teams: { label: "Teams",         icon: Users, gate: ({ has }) => has("settings_teams.view") },
  claude: { label: "Ask AI",        icon: Sparkles, gate: ({ has }) => has("settings_claude.view") },
  apps: { label: "Apps",          icon: AppWindow, gate: ({ has }) => has("settings_apps.view") },
  appdefaults: { label: "App Audiences", icon: AppWindow, gate: ({ has }) => has("settings_app_audiences.view") },
  permissions: { label: "Permissions",   icon: ShieldCheck, gate: ({ has }) => has("settings_permissions.view") },
  requests: { label: "Requests",      icon: GitPullRequest, gate: ({ has }) => has("settings_requests.view") },
  // Iter 115a — the Activity Logs / System Logs tab was removed from Settings;
  // both panels live on the top-nav Activity page (`/activity`).
  documentation: { label: "Documentation", icon: BookOpen, gate: ({ has }) => has("settings_documentation.view") },
  "help-articles": { label: "Help Articles", icon: HelpCircle, gate: ({ has }) => has("settings_help_articles.view") },
  "helpdesk-departments": { label: "Departments", icon: LifeBuoy, gate: ({ has }) => has("settings_helpdesk_departments.view") },
  "helpdesk-priorities": { label: "Priorities",  icon: LifeBuoy, gate: ({ has }) => has("settings_helpdesk_priorities.view") },
  "helpdesk-tiers": { label: "Tiers",       icon: LifeBuoy, gate: ({ has }) => has("settings_helpdesk_tiers.view") },
  "helpdesk-sla": { label: "SLA Policies", icon: LifeBuoy, gate: ({ has }) => has("settings_helpdesk_sla.view") },
  "helpdesk-hours": { label: "Business Hours", icon: LifeBuoy, gate: ({ has }) => has("settings_helpdesk_hours.view") },
  "helpdesk-automation": { label: "Automation",    icon: LifeBuoy, gate: ({ has }) => has("settings_helpdesk_automation.view") },
  "helpdesk-trash": { label: "Deleted Tickets", icon: LifeBuoy, gate: ({ has }) => has("settings_helpdesk_trash.view") },
  developer: { label: "Developer",     icon: KeyRound , gate: ({ has }) => has("settings_developer.view") },
  // Iter 225 — Knowledge Base tools moved here from the KB "Manage KB" menu.
  // Every KB tab needs its own grant AND "Knowledge Base — management tools".
  "kb-import-review": { label: "Import review", icon: GitCompareArrows, gate: ({ has }) => has("kb_admin.view") && has("settings_kb_import_review.view") },
  "kb-helpjuice": { label: "Import HelpJuice", icon: DownloadCloud, gate: ({ has }) => has("kb_admin.view") && has("settings_kb_helpjuice.view") },
  "kb-intercom": { label: "Intercom", icon: DownloadCloud, gate: ({ has }) => has("kb_admin.view") && has("settings_kb_intercom.view") },
  "kb-articles": { label: "Articles", icon: FileText, gate: ({ has }) => has("kb_admin.view") && has("settings_kb_articles.view") },
  "kb-images": { label: "Images", icon: ImageIcon, gate: ({ has }) => has("kb_admin.view") && has("settings_kb_images.view") },
  "kb-branding": { label: "Branding", icon: Palette, gate: ({ has }) => has("kb_admin.view") && has("settings_kb_branding.view") },
};

const GROUP_ICONS = {
  marketing:   MegaphoneIcon,
  engineering: Wrench,
  admin:       ShieldAlert,
  platform:    Server,
  helpdesk:    LifeBuoy,
  kb:          Library,
};

const DEFAULT_TAB_GROUPS = {
  groups: ["marketing", "engineering", "admin", "platform", "helpdesk", "kb"],
  labels: { marketing: "Marketing", engineering: "Engineering", admin: "Admin", platform: "Platform", helpdesk: "Helpdesk", kb: "Knowledge Base" },
  items: {
    marketing:   ["branding", "dashboard", "system-status", "escalation", "maintenance", "news", "help-articles"],
    engineering: ["documentation", "developer"],
    admin:       ["teams", "apps", "appdefaults", "permissions", "staff", "default-org", "requests"],
    platform:    ["platform", "db-connections", "api-connections", "file-storage", "sftp", "uc-cdr", "e911", "notifications", "integrations", "security", "vendor-brands", "claude"],
    helpdesk:    ["helpdesk-departments", "helpdesk-priorities", "helpdesk-tiers", "helpdesk-sla", "helpdesk-hours", "helpdesk-automation", "helpdesk-trash"],
    kb:          ["kb-import-review", "kb-helpjuice", "kb-intercom", "kb-articles", "kb-images", "kb-branding"],
  },
};

export default function SettingsPage() {
  const { has, user } = useAuth();
  const { refresh, apply } = useBranding();
  const [searchParams, setSearchParamsRaw] = useSearchParams();
  const [s, setS] = useState(null);
  const [news, setNews] = useState([]);
  const [recipientCounts, setRecipientCounts] = useState(null);
  const [draft, setDraft] = useState({ title: "", body: "", audience: "partner", pinned: false, shown_date: "", expiry_date: "", author: "", announcement_id: null });
  const fileRef = useRef(null);
  // Iter 111 — Per-theme logo upload refs (light/dark).
  const lightLogoRef = useRef(null);
  const darkLogoRef = useRef(null);
  // Access model v2 — every Settings tab is its own module: settings_<tab>.view / .edit
  const canEdit = (tab) => has(`settings_${tab}.edit`);
  const canManage = has("settings_*.edit");
  // Iter 148 — staff list for the Team leads pickers.
  const [staffOptions, setStaffOptions] = useState([]);
  useEffect(() => {
    api.get("/inout").then((r) => setStaffOptions(r.data?.staff || [])).catch(() => {});
  }, []);

  // Iter 244 — partner self-service. Partner viewers never read or write the
  // platform settings blob (the API 403s them now); their two tabs are bound
  // to their own organization instead.
  const isPartnerViewer = ["partner", "customer"].includes((user?.base_role || user?.role || "").toLowerCase());
  const isPartnerAdmin = isPartnerViewer && (user?.role || "").toLowerCase() === "partner_admin";
  const [myOrg, setMyOrg] = useState(null);
  const [orgBrand, setOrgBrand] = useState(brandingFormOf(null));
  const [orgEscalationDefault, setOrgEscalationDefault] = useState("");
  const [orgSaving, setOrgSaving] = useState(false);

  const loadMyOrg = useCallback(() => {
    api.get("/my-organization").then(({ data }) => {
      setMyOrg(data);
      setOrgBrand(brandingFormOf(data));
      setOrgEscalationDefault(data?.platform_escalation_default || "");
    }).catch(() => {});
  }, []);

  const saveMyBranding = async (patch) => {
    setOrgSaving(true);
    try {
      const payload = { ...orgBrand, ...(patch || {}) };
      const { data } = await api.put("/my-organization/branding", payload);
      setMyOrg(data);
      setOrgBrand(brandingFormOf(data));
      refresh();
      toast.success("Branding updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save branding");
    } finally { setOrgSaving(false); }
  };

  const canManageNews = !!user && (user.role === "super_admin" || (user.base_role === "staff" && (user.teams?.length ? user.teams : [user.team || ""]).some((t) => (t || "").toLowerCase() === "marketing")));
  const isSuper = user?.role === "super_admin";

  // Tab-group layout state — pulled from settings, edited super-admin only.
  // Merge with DEFAULT_TAB_GROUPS so newly-added groups (e.g. Helpdesk in
  // Iter 107) show up automatically for tenants that already saved a
  // custom layout under the older 4-group schema.
  const tabGroups = useMemo(() => {
    const saved = s?.settings_tab_groups;
    if (!saved) return DEFAULT_TAB_GROUPS;
    const mergedItems = { ...DEFAULT_TAB_GROUPS.items };
    Object.entries(saved.items || {}).forEach(([k, v]) => { mergedItems[k] = v; });
    // Ensure every default group has an items entry so its pill can render.
    DEFAULT_TAB_GROUPS.groups.forEach((g) => {
      if (!mergedItems[g]) mergedItems[g] = DEFAULT_TAB_GROUPS.items[g] || [];
    });
    // Iter 110 — Append any default sub-tab keys that are missing from the
    // saved layout entirely (e.g. new "db-connections"/"api-connections"
    // added in a later release). Iter 110c fix: only append when the key
    // is absent from EVERY saved group — otherwise moving a tab between
    // groups via "Edit layout" leaves stale copies in the original group
    // and the activeTab→activeGroup sync effect flip-flops the master
    // pill (e.g. Marketing → Help Articles → clicking Engineering
    // instantly snapped back to Marketing).
    const allSavedKeys = new Set();
    Object.values(mergedItems).forEach((arr) => (arr || []).forEach((k) => allSavedKeys.add(k)));
    DEFAULT_TAB_GROUPS.groups.forEach((g) => {
      const defaults = DEFAULT_TAB_GROUPS.items[g] || [];
      const existing = mergedItems[g] || [];
      const missing = defaults.filter((k) => !existing.includes(k) && !allSavedKeys.has(k));
      if (missing.length) mergedItems[g] = [...existing, ...missing];
    });
    return {
      groups: saved.groups && saved.groups.length ? saved.groups : DEFAULT_TAB_GROUPS.groups,
      labels: { ...DEFAULT_TAB_GROUPS.labels, ...(saved.labels || {}) },
      items:  mergedItems,
    };
  }, [s]);
  const [activeGroup, setActiveGroup] = useState("marketing");
  const [activeTab,   setActiveTab]   = useState(searchParams.get("tab") || "branding");
  const urlTab = searchParams.get("tab");
  useEffect(() => { if (urlTab) setActiveTab(urlTab); }, [urlTab]);
  const [editLayout,  setEditLayout]  = useState(false);
  const [groupDragOver, setGroupDragOver] = useState(null);
  const [itemDragOver,  setItemDragOver]  = useState(null);
  const dragGroupRef = useRef(null);
  const dragItemRef  = useRef(null);

  // Iter 102 — Cross-group filter. Typing here hides the master/sub-tab
  // pills and shows a flat list of matching sub-tabs (with group
  // breadcrumb) instead. Clicking a match jumps to that tab and clears
  // the query. Filter is a plain substring match on the sub-tab label
  // and its group label — good enough for the current tab volume.
  const [tabFilter, setTabFilter] = usePersistedState("settings.tab-filter", "");
  const tabFilterNorm = tabFilter.trim().toLowerCase();
  const filteredMatches = tabFilterNorm
    ? (() => {
        const gates = { has, canManage, isPartnerViewer, isPartnerAdmin };
        const out = [];
        for (const gk of (tabGroups.groups || [])) {
          const gLabel = tabGroups.labels?.[gk] || gk;
          for (const tk of (tabGroups.items?.[gk] || [])) {
            const meta = TAB_META[tk];
            if (!meta) continue;
            if (meta.gate && !meta.gate(gates)) continue;
            const haystack = `${meta.label} ${gLabel}`.toLowerCase();
            if (haystack.includes(tabFilterNorm)) {
              out.push({ groupKey: gk, groupLabel: gLabel, tabKey: tk, meta });
            }
          }
        }
        return out;
      })()
    : [];

  // Iter 103 — Fetch the apps registry once so the Settings filter can
  // also match app names/descriptions and jump straight into the app
  // editor with `?tab=apps&edit=<app_id>`. Failure is non-fatal; the
  // filter just falls back to sub-tab-only matching.
  const [allApps, setAllApps] = useState([]);
  useEffect(() => {
    let cancel = false;
    api.get("/apps").then(({ data }) => {
      if (!cancel) setAllApps(Array.isArray(data) ? data : []);
    }).catch(() => { if (!cancel) setAllApps([]); });
    return () => { cancel = true; };
  }, []);

  const filteredAppHits = tabFilterNorm && has("settings_apps.view")
    ? allApps
        .filter((a) => {
          const hay = `${a.name || ""} ${a.description || ""}`.toLowerCase();
          return hay.includes(tabFilterNorm);
        })
        .slice(0, 12) // don't flood the results row
    : [];

  const jumpToAppInAdmin = (appId) => {
    // Land on the Apps sub-tab (Admin group) and pass `edit=<id>` so
    // AppsPage opens that app's editor dialog on mount.
    setActiveGroup("admin");
    setActiveTab("apps");
    setSearchParamsRaw({ tab: "apps", edit: appId });
    setTabFilter("");
  };

  // Iter 101 — Purge-all-sessions dialog state. Requires the operator to
  // type the exact phrase "confirm" (case-insensitive) before the red
  // button unlocks, guarding against fat-fingered clicks on a nuclear
  // action. Dialog is opened from the Security tab.
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeInput, setPurgeInput] = useState("");
  const [purgeBusy, setPurgeBusy] = useState(false);
  const purgeConfirmed = purgeInput.trim().toLowerCase() === "confirm";
  const purgeAllSessions = async () => {
    if (!purgeConfirmed || purgeBusy) return;
    setPurgeBusy(true);
    try {
      const { data } = await api.post("/settings/sessions/purge");
      toast.warning(`${data.invalidated} session(s) signed out. Redirecting…`);
      setTimeout(() => { window.location.href = "/login"; }, 1200);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to purge sessions");
      setPurgeBusy(false);
    }
  };

  // Iter 91g — Master pill order is now driven by the shared
  // `submenu_orders.settings_groups` bucket (same pattern as Inventory /
  // Number Orders / Fax) so drops auto-save without needing a global
  // "Save changes" click. `tabGroups.items` still owns per-group sub-tab
  // order — that path is unchanged.
  const { orderedKeys: masterOrder, save: saveMasterOrder } =
    useSubmenuOrder("settings_groups", DEFAULT_TAB_GROUPS.groups);
  const masterKeys = useMemo(
    () => {
      const saved = masterOrder && masterOrder.length ? masterOrder : DEFAULT_TAB_GROUPS.groups;
      // Union with defaults so newly-added groups (Iter 107 Helpdesk) can't
      // be missing just because a super-admin saved a layout earlier.
      const out = [...saved];
      DEFAULT_TAB_GROUPS.groups.forEach((g) => { if (!out.includes(g)) out.push(g); });
      return out;
    },
    [masterOrder],
  );
  // Sub-tabs the current user is actually granted — drives the pill
  // counts, the sub-tab row and which group to jump to.
  const visibleItems = (gk) => (tabGroups.items?.[gk] || [])
    .filter((k) => TAB_META[k] && (!TAB_META[k].gate || TAB_META[k].gate({ has, canManage, isPartnerViewer, isPartnerAdmin })));

  // Iter 219 — permission guard on the ACTIVE tab, not just the pills. The
  // default ("branding") and any `?tab=` deep link are only honoured when the
  // user holds that tab's grant; otherwise we land on the first tab they do
  // hold, and with none at all no tab content is mounted.
  const allowedTabs = masterKeys.flatMap(visibleItems);
  const effectiveTab = allowedTabs.includes(activeTab) ? activeTab : (allowedTabs[0] || null);
  useEffect(() => {
    if (effectiveTab && effectiveTab !== activeTab) setActiveTab(effectiveTab);
  }, [effectiveTab, activeTab]);

  // When the active sub-tab changes, keep the master group in sync so
  // the correct sub-row is visible.
  useEffect(() => {
    for (const gk of masterKeys) {
      if ((tabGroups.items?.[gk] || []).includes(activeTab)) {
        setActiveGroup(gk);
        return;
      }
    }
  }, [activeTab, tabGroups, masterKeys]);

  const setTabGroups = (next) => {
    setS((prev) => ({ ...(prev || {}), settings_tab_groups: next }));
  };
  const reorderGroup = (targetKey) => {
    const src = dragGroupRef.current;
    if (!src || src === targetKey) return;
    const groups = [...masterKeys];
    const from = groups.indexOf(src);
    const to   = groups.indexOf(targetKey);
    if (from < 0 || to < 0) return;
    groups.splice(from, 1);
    groups.splice(to, 0, src);
    saveMasterOrder(groups); // auto-saves via /settings.submenu_orders
    dragGroupRef.current = null; setGroupDragOver(null);
  };
  const reorderItem = (targetKey) => {
    const src = dragItemRef.current;
    if (!src || src === targetKey) return;
    const list = [...(tabGroups.items?.[activeGroup] || [])];
    // Cross-group moves — pull src out of its current group first.
    const srcGroup = masterKeys.find((gk) =>
      (tabGroups.items?.[gk] || []).includes(src));
    const items = { ...(tabGroups.items || {}) };
    if (srcGroup && srcGroup !== activeGroup) {
      items[srcGroup] = (items[srcGroup] || []).filter((k) => k !== src);
    } else {
      // Same-group reorder.
      const from = list.indexOf(src); if (from >= 0) list.splice(from, 1);
    }
    const to = list.indexOf(targetKey);
    if (to < 0) list.push(src); else list.splice(to, 0, src);
    items[activeGroup] = list;
    const nextGroups = { ...tabGroups, items };
    setTabGroups(nextGroups);
    // Iter 91h — auto-save sub-tab reorder immediately (matches the
    // master-pill drag pattern), so users don't need to hit the global
    // Save changes button after every re-arrangement.
    autoSaveSubOrder(nextGroups);
    dragItemRef.current = null; setItemDragOver(null);
  };

  // Debounced-style PATCH: writes only `settings_tab_groups` back to
  // `/settings` so the whole settings blob isn't touched on every drop.
  const autoSaveSubOrder = async (nextGroups) => {
    try {
      await api.put("/settings", { settings_tab_groups: nextGroups });
      toast.success("Order saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save order");
    }
  };

  const reloadSettings = () => api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => {
    if (isPartnerViewer) {
      // No platform settings for partners — just enough for the page shell.
      setS({});
      loadMyOrg();
      // the UnitedCloud table doubles as the fallback preview and the
      // "start from" template for the partner's own escalation editor
    } else {
      reloadSettings();
    }
    loadNews();
    if (canManageNews) api.get("/announcements/recipient-counts").then((r) => setRecipientCounts(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const loadNews = () => api.get(`/announcements${canManageNews ? "?all=true" : ""}`).then((r) => setNews(r.data)).catch(() => {});

  const resetDraft = () => setDraft({ title: "", body: "", audience: "partner", pinned: false, shown_date: "", expiry_date: "", author: "", announcement_id: null });

  const addNews = async () => {
    if (!draft.title.trim()) { toast.error("Title is required"); return; }
    try {
      const payload = { title: draft.title, body: draft.body, audience: draft.audience, pinned: draft.pinned, shown_date: draft.shown_date, expiry_date: draft.expiry_date, author: (draft.author || "").trim() };
      if (draft.announcement_id) {
        await api.put(`/announcements/${draft.announcement_id}`, payload);
        toast.success("Announcement updated");
      } else {
        await api.post("/announcements", payload);
        toast.success("Announcement posted");
      }
      resetDraft();
      loadNews();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const editNews = (n) => {
    setDraft({ title: n.title || "", body: n.body || "", audience: n.audience || "partner", pinned: !!n.pinned, shown_date: n.shown_date || "", expiry_date: n.expiry_date || "", author: n.author || "", announcement_id: n.announcement_id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const delNews = async (id) => {
    try { await api.delete(`/announcements/${id}`); toast.success("Removed"); loadNews(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [sending, setSending] = useState(null);
  const sendNews = async (n) => {
    setSending(n.announcement_id);
    try {
      const { data } = await api.post(`/announcements/${n.announcement_id}/send-email`);
      toast.success(data.sent ? `Emailed to ${data.sent} recipient(s)` : (data.detail || "No recipients matched"));
      loadNews();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(null); }
  };

  const save = async () => {
    try {
      const { data } = await api.put("/settings", s);
      // Iter 101 — the backend returns `_sessions_invalidated` when
      // session_timeout_days changes. Show a distinct warning toast and
      // force the current tab back to /login (their own cookie is now
      // dead too — the next API call would 401 anyway).
      if (data?._sessions_invalidated) {
        toast.warning(`Session timeout changed · ${data._sessions_invalidated} active session(s) signed out. Redirecting…`);
        setTimeout(() => { window.location.href = "/login"; }, 1500);
        return;
      }
      setS(data); refresh(); toast.success("Settings saved");
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!s) return <div className="text-slate-400">Loading…</div>;
  const set = (k, v) => setS({ ...s, [k]: v });

  // Iter 110b — Replaces the single top-page "Save changes" button. Each
  // logical section renders its own inline save so operators can commit
  // just the fields they touched without jumping back to the header. The
  // underlying PUT still sends the whole settings blob (backend merges
  // by field), so pressing any Save persists every uncommitted change on
  // the page — but the visual affordance now sits next to the section it
  // belongs to. `save()` toast confirms the write for every button.
  const sectionSave = (testid) => canManage ? (
    <div className="flex justify-end border-t border-slate-100 pt-3">
      <Button onClick={save} data-testid={testid} className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
        <Save className="h-4 w-4" /> Save
      </Button>
    </div>
  ) : null;

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Logo must be under 512KB"); return; }
    const reader = new FileReader();
    reader.onload = () => { set("logo_url", reader.result); apply({ ...s, logo_url: reader.result }); };
    reader.readAsDataURL(file);
  };

  // Iter 111 — Generic per-theme (light / dark) logo uploader. Bound
  // to `logo_url_light` and `logo_url_dark`. Refs are kept local so
  // both file inputs can coexist with the legacy `onLogo` above.
  const onThemeLogo = (which) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) { toast.error("Logo must be under 512KB"); return; }
    const key = which === "light" ? "logo_url_light" : "logo_url_dark";
    const reader = new FileReader();
    reader.onload = () => { set(key, reader.result); apply({ ...s, [key]: reader.result }); };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <PageHeader testid="settings-header" title="Settings" subtitle="Branding, platform configuration and security policies." />

      <Tabs value={effectiveTab || "__no_access__"} onValueChange={setActiveTab}>
        {!effectiveTab && (
          <Card className="mb-4 border-amber-200 bg-amber-50 p-6 text-sm text-amber-900" data-testid="settings-no-access">
            You don&apos;t have access to any settings section. Ask an administrator to grant a <span className="font-mono">Settings tabs</span> permission in the Role Matrix.
          </Card>
        )}
        {/* Iter 77: two-row grouped pill layout. Master pills (Marketing /
            Engineering / Admin / Platform) on top; sub-tab pills for the
            selected master group below. Order edited by super admin only
            via the "Edit layout" toggle. */}
        <div className="mb-4 flex flex-col gap-2" data-testid="settings-tabs">
          {/* Iter 102 — Cross-group filter bar. Typing narrows the whole
              settings tree to matching sub-tabs so operators don't have
              to guess which master group holds "SMTP" or "MFA". */}
          <div className="relative max-w-md" data-testid="settings-filter-bar">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={tabFilter}
              onChange={(e) => setTabFilter(e.target.value)}
              placeholder="Filter settings — e.g. branding, security, integrations…"
              className="h-9 pl-8 pr-8 text-sm"
              data-testid="settings-filter-input"
            />
            {tabFilter && (
              <button
                type="button"
                onClick={() => setTabFilter("")}
                data-testid="settings-filter-clear"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                aria-label="Clear filter">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {tabFilterNorm ? (
            <div className="flex flex-wrap gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid="settings-filter-results">
              {filteredMatches.length === 0 && filteredAppHits.length === 0 && (
                <span className="px-2 py-1 text-xs text-slate-500" data-testid="settings-filter-empty">
                  No settings or apps match &ldquo;{tabFilter}&rdquo;.
                </span>
              )}
              {filteredMatches.map(({ groupKey, groupLabel, tabKey, meta }) => {
                const Icon = meta.icon;
                return (
                  <button
                    key={`${groupKey}:${tabKey}`}
                    type="button"
                    onClick={() => {
                      setActiveGroup(groupKey);
                      setActiveTab(tabKey);
                      setTabFilter("");
                    }}
                    data-testid={`settings-filter-hit-${tabKey}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-transparent bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:border-primary/40 hover:text-primary">
                    <Icon className="h-3.5 w-3.5" />
                    <span>{meta.label}</span>
                    <span className="text-[10px] font-normal text-slate-400">in {groupLabel}</span>
                  </button>
                );
              })}
              {/* Iter 103 — App hits. Each pill opens the Apps admin
                  editor for that app via `?tab=apps&edit=<id>`. Uses
                  the AppWindow icon so app entries visually cluster
                  apart from settings-tab entries. */}
              {filteredAppHits.map((a) => (
                <button
                  key={`app:${a.app_id}`}
                  type="button"
                  onClick={() => jumpToAppInAdmin(a.app_id)}
                  data-testid={`settings-filter-app-${a.app_id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary shadow-sm transition hover:border-primary/40 hover:bg-primary/10">
                  <AppWindow className="h-3.5 w-3.5" />
                  <span>{a.name}</span>
                  <span className="text-[10px] font-normal text-primary/60">app</span>
                </button>
              ))}
            </div>
          ) : (
          <>
          <div className="flex flex-wrap items-center gap-2">
            {masterKeys.filter((gk) => editLayout || visibleItems(gk).length > 0).map((gk) => {
              const label = tabGroups.labels?.[gk] || gk;
              const active = gk === activeGroup;
              const draggable = editLayout;
              const GIcon = GROUP_ICONS[gk];
              return (
                <button key={gk} type="button" role="tab"
                        draggable={draggable}
                        onDragStart={draggable ? (e) => { dragGroupRef.current = gk; try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", gk); } catch { /* noop */ } } : undefined}
                        onDragOver={draggable ? (e) => { e.preventDefault(); setGroupDragOver(gk); } : undefined}
                        onDragLeave={draggable ? () => setGroupDragOver((p) => (p === gk ? null : p)) : undefined}
                        onDrop={draggable ? () => reorderGroup(gk) : undefined}
                        onDragEnd={() => { dragGroupRef.current = null; setGroupDragOver(null); }}
                        onClick={() => {
                          setActiveGroup(gk);
                          // Jump content to the first visible sub-tab of the newly
                          // selected group so the pane always matches the pills.
                          const items = visibleItems(gk);
                          if (items.length && !items.includes(activeTab)) {
                            setActiveTab(items[0]);
                          }
                        }}
                        data-testid={`settings-group-${gk}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
                          active
                            ? "border-primary bg-primary text-primary-foreground shadow-sm"
                            : "border-slate-200 bg-white text-slate-600 hover:border-primary/40 hover:text-slate-900"
                        } ${draggable ? "cursor-grab" : ""} ${groupDragOver === gk ? "ring-2 ring-primary/50" : ""}`}>
                  {GIcon ? <GIcon className="h-4 w-4" /> : null}
                  {label}
                  <span className="ml-1 rounded bg-white/25 px-1.5 py-0.5 text-[10px] font-bold tabular-nums" data-testid={`settings-group-count-${gk}`}>
                    {visibleItems(gk).length}
                  </span>
                </button>
              );
            })}
            {isSuper && (
              <button onClick={() => setEditLayout((v) => !v)}
                      data-testid="settings-edit-layout-btn"
                      className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                {editLayout ? <><Check className="h-3.5 w-3.5" /> Done</> : <><Pencil className="h-3.5 w-3.5" /> Edit layout</>}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 rounded-lg bg-slate-50 p-2">
            {visibleItems(activeGroup)
              .map((tk) => {
              const meta = TAB_META[tk];
              const Icon = meta.icon;
              const active = tk === activeTab;
              const draggable = editLayout;
              return (
                <button key={tk} type="button" role="tab"
                        draggable={draggable}
                        onDragStart={draggable ? (e) => { dragItemRef.current = tk; try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", tk); } catch { /* noop */ } } : undefined}
                        onDragOver={draggable ? (e) => { e.preventDefault(); setItemDragOver(tk); } : undefined}
                        onDragLeave={draggable ? () => setItemDragOver((p) => (p === tk ? null : p)) : undefined}
                        onDrop={draggable ? () => reorderItem(tk) : undefined}
                        onDragEnd={() => { dragItemRef.current = null; setItemDragOver(null); }}
                        onClick={() => setActiveTab(tk)}
                        data-testid={`settings-tab-${tk}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                          active
                            ? "border-primary bg-white text-primary shadow-sm"
                            : "border-transparent bg-white/70 text-slate-600 hover:text-slate-900"
                        } ${draggable ? "cursor-grab" : ""} ${itemDragOver === tk ? "ring-2 ring-primary/50" : ""}`}>
                  <Icon className="h-3.5 w-3.5" /> {meta.label}
                </button>
              );
            })}
            {(tabGroups.items?.[activeGroup] || []).length === 0 && (
              <span className="px-2 py-1 text-xs text-slate-400">No tabs in this group.</span>
            )}
          </div>
          {editLayout && (
            <p className="text-[11px] text-slate-500">
              Drag master pills to reorder groups, or drag sub-tabs within a group.
              Both auto-save on drop — no <b>Save changes</b> click needed.
            </p>
          )}
          </>
          )}
        </div>

        <TabsContent value="branding">
          {isPartnerViewer ? (
          <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="settings-partner-branding-tab">
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-800">White-label branding{myOrg?.name ? ` — ${myOrg.name}` : ""}</h3>
              <p className="text-xs text-slate-500">How your company looks to your users and their customers after login. Leave it switched off to keep the UnitedCloud look.</p>
            </div>
            <PartnerBrandingForm value={orgBrand} onChange={setOrgBrand} partnerName={myOrg?.name}
                                 disabled={!isPartnerAdmin} showEscalationCopy={false} columns={2} />
            {isPartnerAdmin && (
              <div className="flex justify-end border-t border-slate-100 pt-3">
                <Button onClick={() => saveMyBranding()} disabled={orgSaving}
                        data-testid="save-partner-branding-button"
                        className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                  {orgSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
              </div>
            )}
          </Card>
          ) : (
          <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="settings-branding-tab">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:gap-8">
              <div className="space-y-4">
                <div className="space-y-1.5">
              <Label>Logo (legacy · single upload used on both light &amp; dark)</Label>
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                  {s.logo_url ? <img src={s.logo_url} alt="logo" className="h-full w-full object-contain" data-testid="logo-preview" /> : <Palette className="h-5 w-5 text-slate-300" />}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-file-input" />
                <Button type="button" variant="outline" disabled={!canEdit("branding")} onClick={() => fileRef.current?.click()} data-testid="logo-upload-button"><Upload className="h-4 w-4" /> Upload</Button>
                {s.logo_url && <Button type="button" variant="ghost" disabled={!canEdit("branding")} onClick={() => set("logo_url", "")} data-testid="logo-clear-button"><X className="h-4 w-4" /> Remove</Button>}
              </div>
              <p className="text-xs text-slate-400">PNG/SVG, square, under 512KB. Used everywhere unless a light-/dark-mode variant below is set. Also shown as the provider logo on the co-branded Partner QBR PDF cover.</p>
            </div>

            {/* Iter 111 — Per-theme logo pair. Ship a version optimised
                for a white background AND one for a dark background so
                the top-bar logo is always legible regardless of the
                viewer's theme. When either is set it wins over the
                legacy single upload above. */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="branding-theme-logos">
              <div>
                <div className="text-sm font-semibold text-slate-800">Theme-specific logos <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 align-middle">New</span></div>
                <p className="text-xs text-slate-500">Upload a light-mode logo (dark text, sits on white) AND a dark-mode logo (light text, sits on the dark chrome). Each variant, PNG/SVG, under 512KB. When either is set it takes precedence over the legacy single logo above.</p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Light-mode logo */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Light-mode logo</Label>
                  <div className="flex items-center gap-3">
                    <div className="flex h-14 w-24 items-center justify-center rounded-lg border border-slate-200 bg-white overflow-hidden">
                      {s.logo_url_light
                        ? <img src={s.logo_url_light} alt="light logo" className="h-full w-full object-contain" data-testid="logo-light-preview" />
                        : <span className="text-[10px] text-slate-300">No upload</span>}
                    </div>
                    <input ref={lightLogoRef} type="file" accept="image/*" className="hidden" onChange={onThemeLogo("light")} data-testid="logo-light-file-input" />
                    <div className="flex flex-col gap-1">
                      <Button type="button" size="sm" variant="outline" disabled={!canEdit("branding")} onClick={() => lightLogoRef.current?.click()} data-testid="logo-light-upload-button"><Upload className="h-3.5 w-3.5" /> Upload</Button>
                      {s.logo_url_light && <Button type="button" size="sm" variant="ghost" disabled={!canEdit("branding")} onClick={() => set("logo_url_light", "")} data-testid="logo-light-clear-button"><X className="h-3.5 w-3.5" /> Remove</Button>}
                    </div>
                  </div>
                </div>
                {/* Dark-mode logo */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Dark-mode logo</Label>
                  <div className="flex items-center gap-3">
                    <div className="flex h-14 w-24 items-center justify-center rounded-lg border border-slate-700 bg-slate-900 overflow-hidden">
                      {s.logo_url_dark
                        ? <img src={s.logo_url_dark} alt="dark logo" className="h-full w-full object-contain" data-testid="logo-dark-preview" />
                        : <span className="text-[10px] text-slate-500">No upload</span>}
                    </div>
                    <input ref={darkLogoRef} type="file" accept="image/*" className="hidden" onChange={onThemeLogo("dark")} data-testid="logo-dark-file-input" />
                    <div className="flex flex-col gap-1">
                      <Button type="button" size="sm" variant="outline" disabled={!canEdit("branding")} onClick={() => darkLogoRef.current?.click()} data-testid="logo-dark-upload-button"><Upload className="h-3.5 w-3.5" /> Upload</Button>
                      {s.logo_url_dark && <Button type="button" size="sm" variant="ghost" disabled={!canEdit("branding")} onClick={() => set("logo_url_dark", "")} data-testid="logo-dark-clear-button"><X className="h-3.5 w-3.5" /> Remove</Button>}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-slate-400">Sizing tip: aim for ~30–40px tall assets with transparent background. The top-bar renders at 70px height with `object-contain` so wider "wordmark" logos look best.</p>
              {/* Iter 111 — Live top-bar preview reflecting current
                  light + dark uploads. Updates instantly on file pick
                  so admins can eyeball contrast before saving. */}
              <TopBarPreview
                lightSrc={s.logo_url_light || s.logo_url}
                darkSrc={s.logo_url_dark || s.logo_url}
                brandName={s.brand_name || "iplogin"}
                primaryColor={s.primary_color || "#206bc4"}
                className="pt-1"
              />
            </div>
              </div>

              <div className="space-y-4" data-testid="branding-identity-fields">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Brand name</Label><Input data-testid="brand-name-input" disabled={!canEdit("branding")} value={s.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Subtitle</Label><Input data-testid="brand-subtitle-input" disabled={!canEdit("branding")} value={s.brand_subtitle || ""} onChange={(e) => set("brand_subtitle", e.target.value)} /></div>
            </div>
            <div className="space-y-1.5"><Label>Tagline</Label><Input disabled={!canEdit("branding")} value={s.brand_tagline || ""} onChange={(e) => set("brand_tagline", e.target.value)} /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Primary color</Label>
                <div className="flex items-center gap-3">
                  <input type="color" disabled={!canEdit("branding")} value={s.primary_color || "#206bc4"} onChange={(e) => { set("primary_color", e.target.value); apply({ ...s, primary_color: e.target.value }); }} className="h-9 w-14 rounded border border-slate-200" data-testid="primary-color-input" />
                  <Input disabled={!canEdit("branding")} value={s.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="flex-1" />
                </div>
              </div>
              <div className="space-y-1.5"><Label>Support email</Label><Input disabled={!canEdit("branding")} value={s.support_email || ""} onChange={(e) => set("support_email", e.target.value)} /></div>
            </div>
              </div>
            </div>
            {sectionSave("save-branding")}
          </Card>
          )}
        </TabsContent>

        {/* Iter (fork) — the old "Dashboard sources & escalation" tab was split
            into System Status / Escalation Board / Maintenance Banner; the
            Dashboard tab now holds only the hero banner editor. */}
        <TabsContent value="dashboard">
          <Card className="border-slate-200 shadow-sm p-6" data-testid="settings-dashboard-header-tab">
            <DashboardHeaderEditor canManage={canManageNews} />
          </Card>
          <Card className="mt-4 border-slate-200 shadow-sm p-6" data-testid="settings-celebrations-tab">
            <CelebrationsCard canManage={canEdit("dashboard")} />
          </Card>
        </TabsContent>

        <TabsContent value="system-status">
          <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="settings-system-status-tab">
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-800">System Status</h3>
              <p className="text-xs text-slate-500">The status level, message and the Pingdom / incident.io sources behind the top status bar and the Status Page tile.</p>
            </div>
            {/* Iter 90e — auto-upgrade legacy plain-text escalations to
                HTML so the RichTextEditor renders newlines correctly. */}
            {(() => {
              const autoSync = /statuspage\.incident\.io/i.test(s.status_url || "");
              return (
                <>
                  {autoSync && (
                    <div
                      data-testid="status-autosync-banner"
                      className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
                    >
                      <span className="mt-0.5 inline-block h-2 w-2 rounded-full bg-emerald-500" />
                      <span>
                        <strong>Auto-synced from Incident.io</strong> — the level and
                        message below are ignored while the source URL points to an
                        <code className="mx-1 rounded bg-white/60 px-1 py-0.5">statuspage.incident.io</code>
                        page. The banner refreshes every 60 seconds from the public status page.
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Status level {autoSync && <span className="text-[10px] font-normal text-slate-400">(manual fallback)</span>}</Label>
                      <Select value={s.status_level || "operational"} onValueChange={(v) => set("status_level", v)} disabled={!canEdit("system_status") || autoSync}>
                        <SelectTrigger data-testid="status-level-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="operational">🟢 Operational</SelectItem>
                          <SelectItem value="minor">🟠 Minor alert</SelectItem>
                          <SelectItem value="major">🔴 Major alert</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Status message {autoSync && <span className="text-[10px] font-normal text-slate-400">(manual fallback)</span>}</Label>
                      <Input data-testid="status-message-input" disabled={!canEdit("system_status") || autoSync} value={s.status_message || ""} onChange={(e) => set("status_message", e.target.value)} placeholder="All systems operational" /></div>
                  </div>
                </>
              );
            })()}
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2 xl:gap-x-8">
              <div className="space-y-1.5"><Label>Current Status source URL</Label>
                <Input data-testid="status-url-input" disabled={!canEdit("system_status")} value={s.status_url || ""} onChange={(e) => set("status_url", e.target.value)} placeholder="https://statuspage.incident.io/iplogin" />
                <p className="text-[11px] text-slate-500">Tip: point this at an <code>statuspage.incident.io</code> page to auto-sync the banner every 60 seconds. Any other URL keeps the manual level/message above.</p></div>
              <div className="space-y-1.5"><Label>Uptime Stats source URL (Pingdom)</Label>
                <Input data-testid="uptime-url-input" disabled={!canEdit("system_status")} value={s.uptime_url || ""} onChange={(e) => set("uptime_url", e.target.value)} placeholder="https://share.pingdom.com/..." /></div>

              {/* Iter 110d — incident.io Widget API URL. Powers the native
                  Status Page app tile (the vendor page ships X-Frame-Options
                  so it can't be iframed). Enable under incident.io
                  dashboard → Settings → Widget API and paste the URL here. */}
              <div className="space-y-1.5 lg:col-span-2"><Label>Status Page Widget API URL (incident.io)</Label>
                <Input data-testid="incident-widget-url-input" disabled={!canEdit("system_status")} value={s.incident_io_widget_url || ""}
                       onChange={(e) => set("incident_io_widget_url", e.target.value)}
                       placeholder="https://api.statuspage.incident.io/v1/widget/…" />
                <p className="text-[11px] text-slate-500">Enable in incident.io <b>Settings → Widget API</b> for the desired status page, then paste the public URL here. The <code>Status Page</code> app tile renders a native Conduit page from this JSON feed because the hosted status page cannot be iframe-embedded.</p></div>
            </div>

            {/* Iter 91ac — Pingdom banner ↔ check mapping (super_admin only). */}
            <div className="rounded-lg border border-slate-100 bg-slate-50/40 p-3">
              <PingdomBannerMapping canManage={canEdit("system_status")} />
            </div>
            {sectionSave("save-dashboard-sources")}
          </Card>
        </TabsContent>

        <TabsContent value="escalation">
          {isPartnerViewer ? (
          <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="settings-partner-escalation-tab">
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-800">Support &amp; Escalations</h3>
              <p className="text-xs text-slate-500">The escalation table shown on the Dashboard for your users and their customers. Leave it empty to show the UnitedCloud contacts.</p>
            </div>
            <div className="space-y-4" data-testid="partner-escalation-editor">
              <div className="space-y-1.5">
                <EscalationTableEditor
                  value={plaintextToHtml(orgBrand.support_escalations || "")}
                  onChange={(v) => setOrgBrand({ ...orgBrand, support_escalations: v })}
                  disabled={!isPartnerAdmin}
                  testid="partner-escalation-input"
                  helper="Each row is one contact block: label · availability · contact lines. Emails become mailto: links and phone numbers become tel: links automatically." />
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" disabled={!isPartnerAdmin}
                          onClick={() => setOrgBrand({ ...orgBrand, support_escalations: orgEscalationDefault })}
                          data-testid="partner-escalation-template-btn"
                          className="rounded border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    Start from the UnitedCloud table
                  </button>
                  <button type="button" disabled={!isPartnerAdmin || !orgBrand.support_escalations}
                          onClick={() => setOrgBrand({ ...orgBrand, support_escalations: "" })}
                          data-testid="partner-escalation-clear-btn"
                          className="rounded border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                    Clear — show the UnitedCloud contacts
                  </button>
                </div>
                <p className="text-xs text-slate-400">Referral, Agent and Master Agent tiers always show the UnitedCloud contacts.</p>
              </div>
              <div className="space-y-1.5">
                <Label>{orgBrand.support_escalations ? "Your card, as your users will see it" : "UnitedCloud contacts — shown while yours is empty"}</Label>
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="partner-escalation-preview">
                  {(orgBrand.support_escalations || orgEscalationDefault)
                    ? <div className="prose prose-sm max-w-none text-sm text-slate-600 [&_a]:text-primary [&_a]:underline [&_table]:w-full [&_td]:border-b [&_td]:border-slate-200 [&_td]:py-1.5 [&_td]:pr-3 [&_th]:text-left"
                           dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(plaintextToHtml(orgBrand.support_escalations || orgEscalationDefault)) }} />
                    : <p className="text-xs text-slate-400">Nothing configured yet.</p>}
                </div>
              </div>
            </div>
            {isPartnerAdmin && (
              <div className="flex justify-end border-t border-slate-100 pt-3">
                <Button onClick={() => saveMyBranding()} disabled={orgSaving}
                        data-testid="save-partner-escalation-button"
                        className="gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground">
                  {orgSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                </Button>
              </div>
            )}
          </Card>
          ) : (
          <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="settings-escalation-tab">
            <div>
              <h3 className="font-heading text-sm font-semibold text-slate-800">Escalation Board</h3>
              <p className="text-xs text-slate-500">The escalation tables shown on the Dashboard — one for Partners &amp; Staff, one for Customers.</p>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-8">
              <div className="space-y-3" data-testid="escalation-partner-column">
                <div className="space-y-1.5"><Label>Escalation — Partners & Staff</Label>
                  <EscalationTableEditor
                    value={plaintextToHtml(s.escalation_partner || "")}
                    onChange={(v) => set("escalation_partner", v)}
                    disabled={!canEdit("escalation")}
                    testid="escalation-partner-input"
                    helper="Renders as a clean 3-column table on the Dashboard. Rows above map straight to label · hours · contacts columns." />
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={!canEdit("escalation")}
                            onClick={() => set("escalation_partner", ESCALATION_TABLE_TEMPLATE)}
                            data-testid="escalation-partner-template-btn"
                            className="rounded border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                      Reset to UnitedCloud default
                    </button>
                  </div></div>
                {sectionSave("save-escalation-partner")}
              </div>
              <div className="space-y-3" data-testid="escalation-customer-column">
                <div className="space-y-1.5"><Label>Escalation — Customers</Label>
                  <EscalationTableEditor
                    value={plaintextToHtml(s.escalation_customer || "")}
                    onChange={(v) => set("escalation_customer", v)}
                    disabled={!canEdit("escalation")}
                    testid="escalation-customer-input" /></div>
                {sectionSave("save-escalation-customer")}
              </div>
            </div>
          </Card>
          )}
        </TabsContent>

        <TabsContent value="maintenance">
          <Card className="border-slate-200 shadow-sm p-6 space-y-4 max-w-2xl" data-testid="settings-maintenance-tab">
            <h3 className="font-heading text-sm font-semibold text-slate-800">Maintenance Banner</h3>
            <p className="text-xs text-slate-500">The pink maintenance callout in the status bar dropdown, plus the nightly window that recolours the top strip.</p>

            {/* Iter 91g — Marketing-editable "Regular Maintenance" pink
                banner that renders inside the top status bar dropdown. */}
            <div className="space-y-1.5" data-testid="maintenance-banner-editor">
              <Label>Maintenance banner — title (bold)</Label>
              <Input
                data-testid="maintenance-title-input"
                disabled={!canEdit("maintenance")}
                value={s.maintenance_title ?? ""}
                onChange={(e) => set("maintenance_title", e.target.value)}
                placeholder="Regular Maintenance (Daily from Midnight to 2:00 AM MDT)" />
              <Label className="pt-1">Maintenance banner — body</Label>
              <Textarea
                data-testid="maintenance-body-input"
                disabled={!canEdit("maintenance")}
                rows={3}
                value={s.maintenance_body ?? ""}
                onChange={(e) => set("maintenance_body", e.target.value)}
                placeholder="We regularly upgrade and backup systems nightly…" />
              <p className="text-[11px] text-slate-400">
                Appears in the pink callout above <b>Current Status</b> when a user expands the top status bar. Leave both empty to hide the banner.
              </p>
            </div>
            {sectionSave("save-maintenance-banner")}

            {/* Iter 120 — nightly window that recolours the top status strip
                bright yellow, plus an optional one-line note. */}
            <div className="space-y-2 pt-4 border-t border-slate-100" data-testid="maint-window-editor">
              <Label>Nightly maintenance window (status strip turns yellow)</Label>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid="maint-window-enabled"
                  disabled={!canEdit("maintenance")}
                  checked={s.maint_window_enabled !== false}
                  onChange={(e) => set("maint_window_enabled", e.target.checked)} />
                Enabled
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">From</span>
                <select data-testid="maint-window-start" disabled={!canEdit("maintenance")}
                  value={s.maint_window_start_hour ?? 0}
                  onChange={(e) => set("maint_window_start_hour", Number(e.target.value))}
                  className="h-9 rounded-md border border-slate-200 px-2 text-sm">
                  {HOURS.map((h) => <option key={h.v} value={h.v}>{h.label}</option>)}
                </select>
                <span className="text-xs text-slate-500">to</span>
                <select data-testid="maint-window-end" disabled={!canEdit("maintenance")}
                  value={s.maint_window_end_hour ?? 2}
                  onChange={(e) => set("maint_window_end_hour", Number(e.target.value))}
                  className="h-9 rounded-md border border-slate-200 px-2 text-sm">
                  {HOURS.map((h) => <option key={h.v} value={h.v}>{h.label}</option>)}
                </select>
                <span className="text-xs text-slate-400">Mountain Time (follows MDT/MST)</span>
              </div>
              <Label className="pt-1">Tonight&apos;s note (optional, one line)</Label>
              <Input data-testid="maint-window-note" disabled={!canEdit("maintenance")}
                maxLength={120}
                value={s.maint_window_note ?? ""}
                onChange={(e) => set("maint_window_note", e.target.value)}
                placeholder="Upgrading SBC firmware in Calgary" />
              <p className="text-[11px] text-slate-400">
                During the window the top strip turns bright yellow and reads &ldquo;This is a recurring daily
                maintenance window…&rdquo;. The note appears beside it as <b>Tonight: …</b>. A live incident
                always overrides the yellow bar.
              </p>
            </div>
            {sectionSave("save-maint-window")}
          </Card>
        </TabsContent>

        <TabsContent value="news">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card className="border-slate-200 shadow-sm p-6 space-y-4">
              <div className="flex items-center gap-2"><Newspaper className="h-4 w-4 text-primary" /><h3 className="font-heading text-sm font-semibold text-slate-800">{draft.announcement_id ? "Edit announcement" : "New announcement"}</h3><span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Super Admin & Marketing</span></div>
              {canManageNews ? (
                <div className="space-y-2">
                  <Input data-testid="news-title-input" placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                  <RichTextEditor value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })} placeholder="Details — add links, images, formatting…" testid="news-body-input" />
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div className="space-y-1"><Label className="text-xs text-slate-500">Show from (optional)</Label><Input type="date" data-testid="news-shown-date" value={draft.shown_date || ""} onChange={(e) => setDraft({ ...draft, shown_date: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs text-slate-500">Archive after (optional)</Label><Input type="date" data-testid="news-expiry-date" value={draft.expiry_date || ""} onChange={(e) => setDraft({ ...draft, expiry_date: e.target.value })} /></div>
                  </div>
                  {/* Iter 111 — Freeform author byline. Blank = uses the
                      current editor's name (previous default). */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Author byline (optional)</Label>
                    <Input
                      data-testid="news-author-input"
                      maxLength={80}
                      placeholder={`Defaults to “${user?.name || "you"}”`}
                      value={draft.author || ""}
                      onChange={(e) => setDraft({ ...draft, author: e.target.value })}
                    />
                    <p className="text-[10px] text-slate-400">Shown as the byline on the dashboard news card. Leave blank to use your own name.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={draft.audience} onValueChange={(v) => setDraft({ ...draft, audience: v })}>
                      <SelectTrigger data-testid="news-audience-select" className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="partner">Partners & Staff</SelectItem>
                        <SelectItem value="all">Everyone</SelectItem>
                        <SelectItem value="staff">Staff only</SelectItem>
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-1.5 text-sm text-slate-600"><input type="checkbox" checked={draft.pinned} onChange={(e) => setDraft({ ...draft, pinned: e.target.checked })} /> Pin</label>
                    {draft.announcement_id && <Button data-testid="news-cancel-button" variant="ghost" onClick={resetDraft} className="ml-auto text-slate-500"><X className="h-4 w-4" /> Cancel</Button>}
                    <Button data-testid="news-add-button" onClick={addNews} className={`${draft.announcement_id ? "" : "ml-auto"} bg-primary hover:bg-primary/90 text-primary-foreground`}>{draft.announcement_id ? <><Save className="h-4 w-4" /> Save</> : <><Plus className="h-4 w-4" /> Post</>}</Button>
                  </div>
                </div>
              ) : (
                <p className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">Only Super Admins and Marketing staff can add or edit Partner News.</p>
              )}
            </Card>

            <Card className="border-slate-200 shadow-sm p-6 space-y-3" data-testid="news-preview-pane">
              <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-400"><Eye className="h-3.5 w-3.5" /> Live preview</div>
              {(draft.title || draft.body) ? (
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2">
                    {draft.pinned && <Pin className="h-3.5 w-3.5 text-primary" />}
                    <h4 className="font-semibold text-slate-800" data-testid="news-preview-title">{draft.title || "Untitled announcement"}</h4>
                    <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-[10px] capitalize text-slate-500">{{ partner: "Partners & Staff", all: "Everyone", staff: "Staff only" }[draft.audience]}</span>
                  </div>
                  {draft.body ? (
                    <div className="prose prose-sm mt-2 max-w-none text-sm text-slate-600 [&_a]:text-primary [&_a]:underline [&_img]:rounded-lg [&_img]:max-w-full [&_img]:h-auto" data-testid="news-preview-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(draft.body) }} />
                  ) : <p className="mt-2 text-sm text-slate-300">No content yet…</p>}
                  <div className="mt-2 text-[11px] text-slate-500" data-testid="news-preview-byline">
                    By <span className="font-medium text-slate-700">{(draft.author || "").trim() || user?.name || "you"}</span>
                  </div>
                  {(draft.shown_date || draft.expiry_date) && <div className="mt-3 text-[11px] text-slate-400">{draft.shown_date ? `Shows from ${draft.shown_date}` : "Shows immediately"}{draft.expiry_date ? ` · archives after ${draft.expiry_date}` : ""}</div>}
                </div>
              ) : (
                <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 text-center">
                  <Newspaper className="h-6 w-6 text-slate-300" />
                  <p className="text-sm text-slate-400">Start typing to preview how this announcement will appear on the dashboard.</p>
                </div>
              )}
            </Card>
          </div>

          <Card className="mt-4 border-slate-200 shadow-sm p-6">
            <h3 className="mb-2 font-heading text-sm font-semibold text-slate-800">Published & scheduled</h3>
            <div className="divide-y divide-slate-100">
              {news.length === 0 ? <div className="py-6 text-center text-sm text-slate-400">No announcements.</div> : news.map((n) => {
                const today = new Date().toISOString().slice(0, 10);
                const scheduled = n.shown_date && n.shown_date > today;
                const expired = n.expiry_date && n.expiry_date < today;
                return (
                <div key={n.announcement_id} className="flex items-start justify-between gap-3 py-3" data-testid={`settings-news-${n.announcement_id}`}>
                  <div>
                    <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-800">{n.title}
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] capitalize text-slate-500">{n.audience}</span>
                      {scheduled && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`news-scheduled-${n.announcement_id}`}>Scheduled</span>}
                      {expired && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600" data-testid={`news-expired-${n.announcement_id}`}>Archived</span>}
                      {/* Iter 111 — Read receipts. Only visible to
                          editors (this page IS editor-only). Shows
                          count of distinct partner orgs that have
                          opened the article. */}
                      {typeof n.seen_by_partners === "number" && (
                        <span
                          className="inline-flex items-center gap-1 rounded bg-slate-50 border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
                          data-testid={`news-seen-pill-${n.announcement_id}`}
                          title={`${n.seen_by_partners} partner org${n.seen_by_partners === 1 ? "" : "s"} opened this`}
                        >
                          Seen by <b className="text-slate-700">{n.seen_by_partners}</b> partner{n.seen_by_partners === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    {n.body && <div className="text-xs text-slate-400 line-clamp-2">{n.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</div>}
                    {(n.shown_date || n.expiry_date) && <div className="mt-0.5 text-[11px] text-slate-400">{n.shown_date ? `Shows ${n.shown_date}` : "Shows now"}{n.expiry_date ? ` · archives ${n.expiry_date}` : ""}</div>}
                    {n.emailed_at && <div className="mt-0.5 text-[11px] text-emerald-600" data-testid={`news-emailed-${n.announcement_id}`}>Emailed to {n.emailed_count || 0} on {new Date(n.emailed_at).toLocaleDateString()}</div>}
                  </div>
                  {canManageNews && <div className="flex items-center gap-1">
                    {/* Iter 91c — Mass-email button PARKED per user (Feb 2026).
                        Do not remind or re-enable until user explicitly asks.
                        Preview & sender workflow will be redesigned first. */}
                    {false && (() => {
                      const label = { all: "recipients", partner: "partners", staff: "staff" }[n.audience] || "recipients";
                      const count = recipientCounts ? recipientCounts[n.audience] : null;
                      return (
                        <button onClick={() => sendNews(n)} disabled={sending === n.announcement_id} data-testid={`news-send-${n.announcement_id}`} title={count != null ? `Email ${count} ${label}` : "Email this announcement"}
                          className="flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-600 hover:border-primary/40 hover:text-primary disabled:opacity-40">
                          <Mail className="h-3.5 w-3.5" /> {sending === n.announcement_id ? "Sending…" : (count != null ? `Email ${count} ${label}` : "Email")}
                        </button>
                      );
                    })()}
                    <button onClick={() => editNews(n)} data-testid={`news-edit-${n.announcement_id}`} className="text-slate-300 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => delNews(n.announcement_id)} data-testid={`news-delete-${n.announcement_id}`} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>}
                </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="platform">
          <div className="space-y-4">
          <Card className="border-slate-200 shadow-sm p-6 space-y-4" data-testid="settings-platform-card">
            {canEdit("platform") && (
              <a href="/env-drift" className="flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50/60 px-4 py-3 transition-colors hover:bg-teal-50" data-testid="settings-env-drift-link">
                <div>
                  <div className="text-sm font-medium text-teal-900">Environment drift report</div>
                  <div className="text-xs text-teal-800/70">Compare the users and organizations in this environment against preview or production</div>
                </div>
                <span className="text-xs font-semibold text-teal-800">Open →</span>
              </a>
            )}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div><div className="text-sm font-medium text-slate-700">Allow partners to create users</div><div className="text-xs text-slate-400">Partners can manage their own customer users</div></div>
              <Switch disabled={!canEdit("platform")} checked={!!s.allow_partner_user_creation} onCheckedChange={(v) => set("allow_partner_user_creation", v)} data-testid="partner-user-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div><div className="text-sm font-medium text-slate-700">Show "Account security" in account menu</div><div className="text-xs text-slate-400">Hides the account security dialog for everyone platform-wide</div></div>
              <Switch disabled={!canEdit("platform")} checked={s.show_account_security !== false} onCheckedChange={(v) => set("show_account_security", v)} data-testid="show-account-security-switch" />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div><div className="text-sm font-medium text-slate-700">Show "Replay onboarding tour" in account menu</div><div className="text-xs text-slate-400">Hides the guided tour replay entry for everyone platform-wide</div></div>
              <Switch disabled={!canEdit("platform")} checked={s.show_replay_tour !== false} onCheckedChange={(v) => set("show_replay_tour", v)} data-testid="show-replay-tour-switch" />
            </div>
            <JobBoardSandboxToggle />
            {user?.role === "super_admin" && <TimeTrackingReset />}
            <JobBoardRejectionCopyEditor />
            {sectionSave("save-platform")}
            {/* Iter 271 — Outbound email kill switch moved here from the Dashboard
                (the Dashboard now shows a dismissable, read-only status strip). */}
            {has("settings_notifications.view") && (
              <div className="pt-2" data-testid="settings-platform-email-killswitch">
                <div className="mb-2 text-sm font-semibold text-slate-700">Outbound email</div>
                <EmailKillSwitch isSuperAdmin={user?.role === "super_admin"}
                                 currentUserEmail={user?.email} />
              </div>
            )}
          </Card>
          <ScheduledJobsPanel />
          </div>
        </TabsContent>

        <TabsContent value="db-connections">
          {canManage
            ? <DbConnectionsAdmin />
            : <Card className="border-slate-200 shadow-sm p-6 text-sm text-slate-500">Super-admin access required.</Card>}
        </TabsContent>

        <TabsContent value="default-org">
          <DefaultOrgPanel canEdit={canEdit("default_org")} />
        </TabsContent>

        <TabsContent value="file-storage">
          <S3StoragePanel canEdit={canEdit("platform")} />
        </TabsContent>

        <TabsContent value="sftp">
          {has("settings_sftp.view")
            ? <SftpDestinationsPanel />
            : <Card className="border-slate-200 shadow-sm p-6 text-sm text-slate-500">SFTP destinations access required.</Card>}
        </TabsContent>

        <TabsContent value="api-connections">
          {canManage
            ? <ApiConnectionsAdmin />
            : <Card className="border-slate-200 shadow-sm p-6 text-sm text-slate-500">Super-admin access required.</Card>}
        </TabsContent>

        <TabsContent value="uc-cdr">
          {has("uc_cdr.edit")
            ? <UCCDRSettings canEdit={has("uc_cdr.edit")} />
            : <Card className="border-slate-200 shadow-sm p-6 text-sm text-slate-500">UC CDR edit access required.</Card>}
        </TabsContent>

        <TabsContent value="e911">
          {has("e911.edit")
            ? <E911Settings />
            : <Card className="border-slate-200 shadow-sm p-6 text-sm text-slate-500">Emergency Numbers edit access required.</Card>}
        </TabsContent>

        <TabsContent value="notifications">
          <div className="space-y-4">
            <Card className="border-slate-200 shadow-sm p-6 max-w-3xl space-y-4" data-testid="settings-notifications-panel">
              <div>
                <h3 className="font-heading text-base font-semibold text-slate-800">My Reminders</h3>
                <p className="mt-1 text-sm text-slate-500">Notifications you&apos;ve snoozed via the bell. Edit when they should re-fire, or cancel them.</p>
              </div>
              <MyRemindersPanel />
            </Card>
            {has("reports.view") && (
              <Card className="border-slate-200 shadow-sm p-6 max-w-3xl" data-testid="settings-my-email-notifications">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-base font-semibold text-slate-800">My email notifications</h3>
                    <p className="mt-1 text-sm text-slate-500">Choose which automated emails you personally receive. Managed in the Reports tab, alongside report schedules.</p>
                  </div>
                  <Button variant="outline" asChild data-testid="settings-open-email-notifications">
                    <Link to="/reports?tab=notifications">Open my choices</Link>
                  </Button>
                </div>
              </Card>
            )}
            <AdminAlertRecipientsCard />
            <CloudTrailAlertsCard canEdit={canEdit("notifications")} staffOptions={staffOptions} />
            <EmailTemplatesCard canEdit={canEdit("notifications")} />
          </div>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2" data-testid="settings-integrations-grid">
            <div className="space-y-4 min-w-0">
              <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="settings-integrations-panel">
                <div>
                  <h3 className="font-heading text-base font-semibold text-slate-800">My Integrations</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Personal third-party accounts you&apos;ve linked. Connected here once,
                    available everywhere in the platform that needs them.
                  </p>
                </div>
                <MyCalendlyIntegrationRow />
              </Card>
              {/* Iter 103 — Quoter (quoter.com) JWT SSO settings. All 5
                  fields are super-admin editable and persist in
                  db.settings["global"]. */}
              {canEdit("integrations") && <QuoterSettingsCard s={s} canManage={canEdit("integrations")} onSaved={reloadSettings} />}
            </div>
            <div className="space-y-4 min-w-0">
              <MsTeamsPresenceCard canManage={canEdit("integrations")} />
            </div>
            {/* Iter 101 Phase 2 — Conduit-as-OIDC-IdP client registry.
                Table-heavy, so it spans both columns. */}
            {canEdit("integrations") && <div className="min-w-0 xl:col-span-2"><OAuth2ClientsAdmin /></div>}
          </div>
        </TabsContent>

        <TabsContent value="security">
          <div className="space-y-4" data-testid="settings-security-grid">
            <SecurityStatusCard />
            <Card className="border-slate-200 shadow-sm p-6 space-y-4" data-testid="security-policy-card">
              <div>
                <h3 className="font-heading text-sm font-semibold text-slate-800">Access policies</h3>
                <p className="text-xs text-slate-400">Session enforcement and IP/domain gates that apply platform-wide.</p>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <div><div className="text-sm font-medium text-slate-700">Enforce two-factor authentication</div><div className="text-xs text-slate-400">Require staff &amp; partners to set up an authenticator app at next sign-in</div></div>
                <Switch disabled={!canEdit("security")} checked={!!s.enforce_mfa} onCheckedChange={(v) => set("enforce_mfa", v)} data-testid="enforce-mfa-switch" />
              </div>
              {/* Iter 101 — Session timeout. Range 1..14 days (clamped
                  server-side). Saving a new value invalidates every
                  active session — the actor gets bounced to /login. */}
              <div className="rounded-lg border border-slate-200 px-4 py-3 space-y-2" data-testid="session-timeout-row">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700">Session timeout</div>
                    <div className="text-xs text-slate-400">
                      How long a signed-in session stays valid before requiring re-authentication.
                      Range 1–14 days. Changing this signs out <span className="font-medium text-amber-700">everyone (including you)</span> immediately.
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Input
                      type="number"
                      min={1}
                      max={14}
                      disabled={!canEdit("security")}
                      value={s.session_timeout_days ?? 7}
                      onChange={(e) => set("session_timeout_days", Math.max(1, Math.min(14, Number(e.target.value) || 1)))}
                      className="w-20"
                      data-testid="session-timeout-input" />
                    <span className="text-xs text-slate-500">days</span>
                    {canEdit("security") && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (window.confirm(`Set session timeout to ${s.session_timeout_days || 7} day(s)? This will sign out every active user immediately — including you.`)) {
                            save();
                          }
                        }}
                        data-testid="session-timeout-save"
                        className="gap-1.5">
                        <Save className="h-3.5 w-3.5" /> Save
                      </Button>
                    )}
                  </div>
                </div>
                {/* Iter 101 — Sign-out-all button. Distinct destructive
                    action from the timeout Save above. Opens a type-to-
                    confirm modal to guard against accidental clicks. */}
                {canEdit("security") && (
                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
                    <div className="min-w-0 text-xs text-slate-500">
                      Sign every active session out right now — useful after a suspected credential leak or role change.
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => { setPurgeInput(""); setPurgeOpen(true); }}
                      data-testid="purge-sessions-open"
                      className="shrink-0 gap-1.5 bg-red-600 text-white hover:bg-red-700">
                      <LogOut className="h-3.5 w-3.5" /> Sign out all sessions
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-1.5"><Label>Iframe domain allowlist (comma separated)</Label>
                <div className="flex gap-2">
                  <Input data-testid="allowlist-input" disabled={!canEdit("security")}
                    value={(s.iframe_allowlist || []).join(", ")}
                    onChange={(e) => set("iframe_allowlist", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }} />
                  {canEdit("security") && (
                    <Button variant="outline" onClick={save}
                            data-testid="iframe-allowlist-save"
                            className="shrink-0 gap-1.5">
                      <Save className="h-3.5 w-3.5" /> Save
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-1.5"><Label>Vendor cost IP allowlist (comma separated)</Label>
                <div className="flex gap-2">
                  <Input data-testid="cost-ip-allowlist-input" disabled={!canEdit("security")}
                    placeholder="e.g. 203.0.113.5, 198.51.100.0/24 — leave empty to allow all staff"
                    value={(s.cost_ip_allowlist || []).join(", ")}
                    onChange={(e) => set("cost_ip_allowlist", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }} />
                  {canEdit("security") && (
                    <Button variant="outline" onClick={save}
                            data-testid="cost-ip-allowlist-save"
                            className="shrink-0 gap-1.5">
                      <Save className="h-3.5 w-3.5" /> Save
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400">Vendor (UC) costs are staff-only. When IPs are listed here, staff must be on an allowed IP/CIDR to see costs. Empty = no IP restriction. Press <kbd className="rounded border border-slate-300 bg-slate-50 px-1 text-[10px]">Enter</kbd> or click Save.</p>
              </div>
            </Card>
            <InternalIpPanel canManage={canEdit("security")} />
            {user?.role === "super_admin" && <DataSyncOperatorsCard />}
            {canEdit("security") && <PasswordPolicyCard s={s} onSaved={reloadSettings} canManage={canEdit("security")} />}
          </div>
        </TabsContent>

        {has("settings_staff.view") && (
          <TabsContent value="staff"><UsersPage scope="staff" embedded /></TabsContent>
        )}
        {has("settings_apps.view") && (
          <TabsContent value="apps">
            {isSuper && <ChangelogResetCard />}
            <AppsPage embedded />
          </TabsContent>
        )}
        {has("settings_app_audiences.view") && (
          <TabsContent value="appdefaults">
            <div className="space-y-4">
              <AppAudiencesCard />
              <AppAudienceGrid />
              <AppAudienceChangesCard />
            </div>
          </TabsContent>
        )}
        {has("settings_permissions.view") && (
          <TabsContent value="permissions"><Permissions /></TabsContent>
        )}
        {has("settings_requests.view") && (
          <TabsContent value="requests"><Requests /></TabsContent>
        )}
        <TabsContent value="developer"><Developer /></TabsContent>
        {has("settings_documentation.view") && (
            <TabsContent value="documentation">
              <Card className="border-slate-200 shadow-sm p-6 max-w-2xl space-y-5" data-testid="settings-documentation">
                <div>
                  <h3 className="font-heading text-base font-semibold text-slate-800">Documentation</h3>
                  <p className="mt-1 text-sm text-slate-500">Regenerate Word documents on demand — they reflect the current live state of Conduit each time you press the button.</p>
                </div>
                <DocumentationPanel />
              </Card>
            </TabsContent>
        )}
        {has("settings_help_articles.view") && (
            <TabsContent value="help-articles">
              <HelpArticlesAdmin />
            </TabsContent>
        )}
        {has("kb_admin.view") && has("settings_kb_import_review.view") && (
          <TabsContent value="kb-import-review"><KBImportReview /></TabsContent>
        )}
        {has("kb_admin.view") && has("settings_kb_helpjuice.view") && (
          <TabsContent value="kb-helpjuice"><KBHelpjuicePreview /></TabsContent>
        )}
        {has("kb_admin.view") && has("settings_kb_intercom.view") && (
          <TabsContent value="kb-intercom"><KbIntercomImportPanel /></TabsContent>
        )}
        {has("kb_admin.view") && has("settings_kb_articles.view") && (
          <TabsContent value="kb-articles"><KbArticlesPanel /></TabsContent>
        )}
        {has("kb_admin.view") && has("settings_kb_images.view") && (
          <TabsContent value="kb-images"><KbImagesPanel /></TabsContent>
        )}
        {has("kb_admin.view") && has("settings_kb_branding.view") && (
          <TabsContent value="kb-branding"><KBSettings /></TabsContent>
        )}
        {has("settings_teams.view") && (
          <TabsContent value="teams">
            <Card className="border-slate-200 shadow-sm p-6 space-y-5" data-testid="teams-editor">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 xl:gap-8">
                <div className="space-y-3" data-testid="team-names-column">
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-slate-800">Team names</h3>
                    <p className="text-xs text-slate-400">Used in the Team dropdown when editing staff. Click Save below to apply.</p>
                  </div>
                  <StringListEditor items={s.team_names || []} disabled={!canEdit("teams")} onChange={(v) => set("team_names", v)} placeholder="New team name" addLabel="Add team" testid="teams" />
                </div>

                {/* Iter 148 — one lead per team, shown first on the team page
                    and the teams directory. */}
                <div className="space-y-3" data-testid="team-leads-column">
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-slate-800">Team leads</h3>
                    <p className="text-xs text-slate-400">The lead appears first on the team page and on the teams directory.</p>
                  </div>
                  <div className="space-y-2" data-testid="team-leads-editor">
                    {(s.team_names || []).map((t) => (
                      <div key={t} className="flex items-center gap-2">
                        <span className="w-28 shrink-0 truncate text-xs font-medium text-slate-600">{t}</span>
                        <Select value={(s.team_leads || {})[t] || "none"} disabled={!canEdit("teams")}
                          onValueChange={(v) => { const next = { ...(s.team_leads || {}) }; if (v === "none") delete next[t]; else next[t] = v; set("team_leads", next); }}>
                          <SelectTrigger className="h-8 flex-1 text-xs" data-testid={`team-lead-select-${t}`}>
                            <SelectValue placeholder="No lead" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No lead</SelectItem>
                            {staffOptions.filter((u) => (u.teams?.length ? u.teams : [u.team]).includes(t))
                              .map((u) => <SelectItem key={u.user_id} value={u.user_id} data-testid={`team-lead-option-${t}-${u.user_id}`}>{u.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Iter 111 — Curated Partner Territories list. Feeds the
                    Territory dropdown on the Partner org form. Missing
                    values still typeable via "Custom / Other" so this
                    isn't a hard gate, just a mistype prevention. */}
                <div className="space-y-3 lg:border-l lg:border-slate-100 lg:pl-6" data-testid="territories-column">
                  <div>
                    <h3 className="font-heading text-sm font-semibold text-slate-800">Partner territories</h3>
                    <p className="text-xs text-slate-400">Powers the Territory dropdown on partner org records. Values must match the Audit Log <code className="font-mono">territory</code> column exactly (e.g. <code className="font-mono">ngenuity.ca</code>).</p>
                  </div>
                  <StringListEditor
                    items={s.territories || []}
                    disabled={!canEdit("teams")}
                    onChange={(v) => set("territories", v)}
                    placeholder="New territory (e.g. example.ca)"
                    addLabel="Add territory"
                    testid="territories"
                  />
                  {canEdit("teams") && (
                    <PullTerritoriesFromNsButton
                      key={s.updated_at || "initial"}
                      onMerge={(added) => {
                        const cur = s.territories || [];
                        const have = new Set(cur.map((t) => String(t).trim()));
                        const fresh = added.filter((t) => !have.has(t));
                        set("territories", [...cur, ...fresh]);
                        return fresh.length;
                      }}
                    />
                  )}
                </div>
              </div>
              {sectionSave("save-teams")}
            </Card>
          </TabsContent>
        )}
        {/* Iter 107 — Helpdesk admin taxonomy (Departments / Priorities /
            Tiers). Read-open to anyone with `helpdesk.view`; edit-gated
            to `settings.manage` inside the editor itself. */}
        {has("settings_helpdesk_*.view") && (
          <>
            <TabsContent value="helpdesk-departments">
              <HelpdeskDepartmentsAdmin canManage={canEdit("helpdesk_departments")} />
            </TabsContent>
            <TabsContent value="helpdesk-priorities">
              <HelpdeskPrioritiesAdmin canManage={canEdit("helpdesk_priorities")} />
            </TabsContent>
            <TabsContent value="helpdesk-tiers">
              <HelpdeskTiersAdmin canManage={canEdit("helpdesk_tiers")} />
            </TabsContent>
            <TabsContent value="helpdesk-sla">
              <HelpdeskSlaPoliciesAdmin canManage={canEdit("helpdesk_sla")} />
            </TabsContent>
            <TabsContent value="helpdesk-hours">
              <HelpdeskBusinessHoursAdmin canManage={canEdit("helpdesk_hours")} />
            </TabsContent>
            <TabsContent value="helpdesk-automation">
              <HelpdeskAutomationAdmin canManage={canEdit("helpdesk_automation")} />
            </TabsContent>
            <TabsContent value="helpdesk-trash">
              <HelpdeskDeletedAdmin canManage={canEdit("helpdesk_trash")} />
            </TabsContent>
          </>
        )}
        {has("settings_vendor_brands.view") && (
          <TabsContent value="vendor-brands">
            <VendorBrandsAdmin canManage={canEdit("vendor_brands")} />
          </TabsContent>
        )}
        {has("settings_claude.view") && (
          <TabsContent value="claude">
            <Card className="border-slate-200 shadow-sm p-6 max-w-2xl space-y-5" data-testid="claude-prompts-editor">
              <div>
                <h3 className="font-heading text-sm font-semibold text-slate-800">Suggested &ldquo;Ask AI&rdquo; prompts</h3>
                <p className="text-xs text-slate-400">Shared prompts show for everyone; role prompts add on top for that user type. Up to 8 are shown. Click Save below to apply.</p>
              </div>
              {[
                { key: "shared", label: "Shared (all users)" },
                { key: "staff", label: "Staff only" },
                { key: "partner", label: "Partners only" },
                { key: "customer", label: "Customers only" },
              ].map((grp) => (
                <div key={grp.key} className="space-y-2">
                  <Label>{grp.label}</Label>
                  <StringListEditor
                    items={(s.claude_prompts && s.claude_prompts[grp.key]) || []}
                    disabled={!canEdit("claude")}
                    onChange={(v) => set("claude_prompts", { ...(s.claude_prompts || {}), [grp.key]: v })}
                    placeholder="New prompt suggestion"
                    addLabel="Add prompt"
                    testid={`claude-${grp.key}`}
                  />
                </div>
              ))}
              {sectionSave("save-claude-prompts")}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Iter 101 — Type-to-confirm purge-all-sessions dialog. Rendered
          at the root so it works from any Settings tab (though the
          trigger only exists in Security). */}
      <Dialog open={purgeOpen} onOpenChange={(v) => { if (!purgeBusy) { setPurgeOpen(v); if (!v) setPurgeInput(""); } }}>
        <DialogContent className="max-w-md" data-testid="purge-sessions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Sign out every active session?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2 text-sm text-slate-600">
              <div>
                This will <span className="font-semibold text-red-700">immediately invalidate every signed-in session</span>, on every device, for every user of Conduit — including your own.
              </div>
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-slate-500">
                <li>Everyone will be bounced to the login screen on their next click.</li>
                <li>You&apos;ll also be signed out and this browser tab will redirect to <span className="font-mono">/login</span>.</li>
                <li>An audit-log entry will record who triggered the purge and how many sessions were affected.</li>
                <li>There is no undo. Use this only after a credential leak, role compromise, or similar security event.</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <Label htmlFor="purge-confirm-input" className="text-xs font-medium text-slate-700">
              Type <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-red-700">confirm</span> to enable the button:
            </Label>
            <Input
              id="purge-confirm-input"
              data-testid="purge-sessions-input"
              value={purgeInput}
              onChange={(e) => setPurgeInput(e.target.value)}
              placeholder="confirm"
              autoComplete="off"
              disabled={purgeBusy}
              className={purgeConfirmed ? "border-red-400 focus-visible:ring-red-400" : ""} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPurgeOpen(false)} disabled={purgeBusy} data-testid="purge-sessions-cancel">
              Cancel
            </Button>
            <Button
              onClick={purgeAllSessions}
              disabled={!purgeConfirmed || purgeBusy}
              data-testid="purge-sessions-confirm"
              className="bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300 disabled:hover:bg-red-300">
              {purgeBusy ? (<><Loader2 className="h-3.5 w-3.5 animate-spin" /> Signing everyone out…</>)
                         : (<><LogOut className="h-3.5 w-3.5" /> Sign out all sessions</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DocumentationPanel() {
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [guideBusy, setGuideBusy] = useState(false);
  const [handoffMeta, setHandoffMeta] = useState(null); // { generated_at, size }
  const [guideMeta, setGuideMeta] = useState(null);

  const generate = async (kind) => {
    const setBusy = kind === "handoff" ? setHandoffBusy : setGuideBusy;
    const setMeta = kind === "handoff" ? setHandoffMeta : setGuideMeta;
    setBusy(true);
    try {
      const { data } = await api.post(`/docs/${kind}`);
      if (!data?.token) throw new Error("No download token");
      await downloadFromUrl(`/files/${data.token}`);
      setMeta({ generated_at: new Date().toLocaleString(), size: data.size });
      toast.success(kind === "handoff" ? "DevOps handoff regenerated" : "User guide regenerated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Regeneration failed");
    } finally {
      setBusy(false);
    }
  };

  const kb = (n) => (n ? `${Math.round(n / 1024).toLocaleString()} KB` : "");

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4 rounded-lg border border-slate-200 p-4" data-testid="docs-handoff-card">
        <FileText className="h-8 w-8 shrink-0 text-primary" />
        <div className="flex-1">
          <div className="font-heading text-sm font-semibold text-slate-800">DevOps Handoff (.docx)</div>
          <p className="mt-0.5 text-xs text-slate-500">
            Technical handoff document — architecture, env vars, integrations, DB collections, API endpoints, code layout, deployment &amp; runbooks. Text-only, refreshes from live DB counts. Instant.
          </p>
          {handoffMeta && (
            <p className="mt-1.5 text-[11px] text-slate-400">Last generated: {handoffMeta.generated_at} · {kb(handoffMeta.size)}</p>
          )}
        </div>
        <Button
          data-testid="docs-handoff-btn"
          onClick={() => generate("handoff")}
          disabled={handoffBusy}
          className="shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {handoffBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {handoffBusy ? "Generating…" : "Regenerate DevOps Handoff"}
        </Button>
      </div>

      <div className="flex items-start gap-4 rounded-lg border border-slate-200 p-4" data-testid="docs-userguide-card">
        <BookOpen className="h-8 w-8 shrink-0 text-emerald-600" />
        <div className="flex-1">
          <div className="font-heading text-sm font-semibold text-slate-800">User Guide with Screenshots (.docx)</div>
          <p className="mt-0.5 text-xs text-slate-500">
            KB-ready guide for staff and partners — one page per major screen with a fresh screenshot from your live app.
            <br /><span className="text-amber-700">Takes ~45 seconds</span> as headless Chromium captures each page.
          </p>
          {guideMeta && (
            <p className="mt-1.5 text-[11px] text-slate-400">Last generated: {guideMeta.generated_at} · {kb(guideMeta.size)}</p>
          )}
        </div>
        <Button
          data-testid="docs-userguide-btn"
          onClick={() => generate("user-guide")}
          disabled={guideBusy}
          className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          {guideBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {guideBusy ? "Capturing screenshots…" : "Regenerate User Guide"}
        </Button>
      </div>

      <p className="text-[11px] text-slate-400">
        Both files download to your browser and are dated at the moment of generation. Files are not stored on the server — always fresh, always current.
      </p>
    </div>
  );
}

function StringListEditor({ items, onChange, disabled, placeholder, addLabel, testid }) {
  const update = (idx, val) => { const next = [...items]; next[idx] = val; onChange(next); };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, ""]);
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <Input data-testid={`${testid}-item-${idx}`} disabled={disabled} value={it} placeholder={placeholder} onChange={(e) => update(idx, e.target.value)} />
          {!disabled && <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-slate-400 hover:text-red-500" data-testid={`${testid}-remove-${idx}`} onClick={() => remove(idx)}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
      {!disabled && <Button type="button" variant="outline" size="sm" data-testid={`${testid}-add`} onClick={add}><Plus className="h-4 w-4" /> {addLabel}</Button>}
    </div>
  );
}


function MyCalendlyIntegrationRow() {
  // Small wrapper around the shared Calendly pill so it lives on the Settings
  // "My Integrations" tab with proper context copy. Once connected, the pill
  // itself becomes the disconnect trigger via its popover.
  return (
    <div className="flex items-start justify-between rounded-lg border border-slate-200 bg-white px-4 py-3.5 gap-4"
         data-testid="settings-integration-calendly-row">
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-9 w-9 shrink-0 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-bold text-sm">
          C
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800">Calendly</div>
          <div className="mt-0.5 text-xs text-slate-500 leading-relaxed">
            Meetings booked by AI Outbound campaigns you own will land on your
            personal Calendly. Connect once and it&apos;s used across every campaign.
          </div>
        </div>
      </div>
      <div className="shrink-0 self-center">
        <CalendlyConnectPill nextPath="/settings?tab=integrations" />
      </div>
    </div>
  );
}




function MyRemindersPanel() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/notifications/reminders").then((r) => setItems(r.data.reminders || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
  };
  const updateDue = async (r, localVal) => {
    setSaving(r.reminder_id);
    try {
      const dueUtc = new Date(localVal).toISOString();
      await api.patch(`/notifications/reminders/${r.reminder_id}`, { due_at: dueUtc });
      toast.success("Reminder updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
    finally { setSaving(null); }
  };
  const cancel = async (r) => {
    if (!window.confirm(`Cancel this reminder?\n\n"${r.title}"`)) return;
    setSaving(r.reminder_id);
    try { await api.delete(`/notifications/reminders/${r.reminder_id}`); toast.success("Reminder cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed"); }
    finally { setSaving(null); }
  };

  if (items == null) return <div className="text-sm text-slate-400" data-testid="reminders-loading">Loading…</div>;
  if (!items.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 px-4 py-8 text-center" data-testid="reminders-empty">
        <Clock className="mx-auto h-6 w-6 text-slate-300" />
        <p className="mt-2 text-sm text-slate-500">No snoozed reminders. Snooze any notification from the bell to see it here.</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="reminders-list">
      {items.map((r) => (
        <div key={r.reminder_id} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4" data-testid={`reminder-row-${r.reminder_id}`}>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-slate-800" data-testid={`reminder-title-${r.reminder_id}`}>{r.title}</div>
            {r.body && <div className="truncate text-xs text-slate-500">{r.body}</div>}
            <div className="mt-0.5 text-[11px] text-slate-400">Snoozed {new Date(r.created_at).toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-2">
            <Input type="datetime-local" className="w-52" data-testid={`reminder-due-${r.reminder_id}`}
              defaultValue={toLocalInput(r.due_at)}
              onBlur={(e) => { if (e.target.value && new Date(e.target.value).toISOString() !== new Date(r.due_at).toISOString()) updateDue(r, e.target.value); }}
              disabled={saving === r.reminder_id} />
            <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500"
              data-testid={`reminder-cancel-${r.reminder_id}`} onClick={() => cancel(r)} disabled={saving === r.reminder_id}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}


/**
 * Iter 103 — Quoter SSO settings card.
 *
 * Self-contained: owns its own local state, dirty tracking, and a
 * scoped "Save Quoter SSO" button (same pattern as OAuth2's "OIDC
 * endpoint URLs" panel). The parent's top-of-page "Save changes"
 * button no longer touches these fields — this card commits them
 * directly via PUT /api/settings.
 *
 * Renders the 5 fields required to mint a Quoter deep-link:
 *   • socketKey (HS256 signing secret — server masks to `***` on read)
 *   • iss       (JWT `iss` claim)
 *   • aud       (JWT `aud` claim)
 *   • sso_url   (base URL; JWT is appended)
 *   • url       (safe fallback shell when SSO can't be minted)
 */
// Remounted by the parent (key = settings.updated_at) so `pulled` resets
// after a Save; a page reload resets it too. One pull per unsaved edit.
function PullTerritoriesFromNsButton({ onMerge }) {
  const [busy, setBusy] = useState(false);
  const [pulled, setPulled] = useState(false);
  const pull = async () => {
    if (busy || pulled) return;
    setBusy(true);
    try {
      const { data } = await api.get("/settings/ns-api/resellers");
      const added = (data.new || []).map((r) => r.reseller);
      const n = added.length ? onMerge(added) : 0;
      setPulled(true);
      if (!n) toast.info(`UC Platform returned ${data.count} reseller(s) — all already in the list`);
      else toast.success(`Added ${n} new territor${n === 1 ? "y" : "ies"} from UC Platform — click Save to apply`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to pull resellers from UC Platform");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="button" variant="outline" size="sm" disabled={busy || pulled} onClick={pull} data-testid="territories-pull-ns">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Pull from UC
      </Button>
      {pulled && <span className="text-xs text-slate-400" data-testid="territories-pull-ns-done">Pulled — save or reload to pull again</span>}
    </div>
  );
}

function QuoterSettingsCard({ s, canManage, onSaved }) {
  const initial = () => ({
    quoter_socket_key:   s?.quoter_socket_key   || "",
    quoter_iss:          s?.quoter_iss          || "",
    quoter_aud:          s?.quoter_aud          || "",
    quoter_sso_url:      s?.quoter_sso_url      || "",
    quoter_fallback_url: s?.quoter_fallback_url || "",
  });
  const [form, setForm] = useState(initial);
  const [baseline, setBaseline] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, checks, quoter_username, error}
  // Re-sync when the parent's `s` refreshes (e.g. after another save).
  useEffect(() => {
    const next = {
      quoter_socket_key:   s?.quoter_socket_key   || "",
      quoter_iss:          s?.quoter_iss          || "",
      quoter_aud:          s?.quoter_aud          || "",
      quoter_sso_url:      s?.quoter_sso_url      || "",
      quoter_fallback_url: s?.quoter_fallback_url || "",
    };
    setForm(next);
    setBaseline(next);
  }, [s?.quoter_socket_key, s?.quoter_iss, s?.quoter_aud, s?.quoter_sso_url, s?.quoter_fallback_url]);

  const dirty = Object.keys(form).some((k) => form[k] !== baseline[k]);
  const socketKeySet = baseline.quoter_socket_key === "***";
  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    if (!dirty || saving || !canManage) return;
    setSaving(true);
    try {
      const payload = { ...form };
      // Server preserves the stored key when it receives the mask value.
      // Trim leading/trailing whitespace on every field.
      Object.keys(payload).forEach((k) => { payload[k] = (payload[k] || "").trim(); });
      await api.put("/settings", payload);
      toast.success("Quoter SSO saved");
      // Ask the parent to refresh so `s` reflects the new state (and
      // the socketKey is re-masked to `***` if it was set).
      if (typeof onSaved === "function") await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save Quoter SSO");
    } finally {
      setSaving(false);
    }
  };
  const revert = () => setForm(baseline);

  const runSelftest = async () => {
    if (testing || dirty) return; // require save-first so the test uses persisted values
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/quoter/selftest");
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || "Selftest failed", checks: [] });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm space-y-4" data-testid="settings-quoter-panel">
      <div>
        <h3 className="font-heading text-base font-semibold text-slate-800">Quoter SSO</h3>
        <p className="mt-1 text-sm text-slate-500">
          JWT SSO for the <b>Quotes</b> app. Fill these in and the app will auto-sign users in with their <code>quoter_username</code>.
          Leave any blank to disable SSO — users get the &ldquo;Open in a new tab&rdquo; fallback instead.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label htmlFor="quoter-socket-key" className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            socketKey
            {socketKeySet && (
              <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid="quoter-socket-key-set">Set</span>
            )}
          </Label>
          <Input
            id="quoter-socket-key"
            type="password"
            autoComplete="new-password"
            value={form.quoter_socket_key}
            onChange={change("quoter_socket_key")}
            placeholder={socketKeySet ? "Leave as ‘***’ to keep the current key" : "HS256 shared secret"}
            disabled={!canManage || saving}
            className="font-mono text-xs"
            data-testid="quoter-socket-key-input"
          />
          <p className="text-[11px] text-slate-400">
            The HS256 signing secret Quoter shared with you. Masked when reading — leave the &lsquo;***&rsquo; alone to keep the stored value.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="quoter-iss" className="text-xs font-semibold uppercase tracking-wider text-slate-600">iss</Label>
          <Input
            id="quoter-iss"
            value={form.quoter_iss}
            onChange={change("quoter_iss")}
            placeholder="https://conduit.iplogin.ca"
            disabled={!canManage || saving}
            className="font-mono text-xs"
            data-testid="quoter-iss-input"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="quoter-aud" className="text-xs font-semibold uppercase tracking-wider text-slate-600">aud</Label>
          <Input
            id="quoter-aud"
            value={form.quoter_aud}
            onChange={change("quoter_aud")}
            placeholder="https://iplogin.quoter.com"
            disabled={!canManage || saving}
            className="font-mono text-xs"
            data-testid="quoter-aud-input"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="quoter-sso-url" className="text-xs font-semibold uppercase tracking-wider text-slate-600">sso_url</Label>
          <Input
            id="quoter-sso-url"
            value={form.quoter_sso_url}
            onChange={change("quoter_sso_url")}
            placeholder="https://iplogin.quoter.com/users/sso/"
            disabled={!canManage || saving}
            className="font-mono text-xs"
            data-testid="quoter-sso-url-input"
          />
          <p className="text-[11px] text-slate-400">Base URL; the minted JWT is appended.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="quoter-fallback-url" className="text-xs font-semibold uppercase tracking-wider text-slate-600">url</Label>
          <Input
            id="quoter-fallback-url"
            value={form.quoter_fallback_url}
            onChange={change("quoter_fallback_url")}
            placeholder="https://unitedcloud.socketapp.com/"
            disabled={!canManage || saving}
            className="font-mono text-xs"
            data-testid="quoter-fallback-url-input"
          />
          <p className="text-[11px] text-slate-400">Safe fallback shell opened in a new tab when SSO can&apos;t be minted.</p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        {dirty && (
          <span className="text-[11px] italic text-amber-700" data-testid="quoter-dirty">Unsaved changes</span>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={runSelftest}
          disabled={testing || dirty || !canManage}
          data-testid="quoter-selftest">
          {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          Test SSO for my user
        </Button>
        {dirty && (
          <Button variant="outline" size="sm" onClick={revert} disabled={saving} data-testid="quoter-revert">
            Revert
          </Button>
        )}
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving || !canManage}
          data-testid="quoter-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save Quoter SSO
        </Button>
      </div>

      {/* Iter 103 — Selftest result panel. Renders only after the button
          has been clicked. Green when everything passes; amber when the
          mint failed with a per-check breakdown so operators can see
          which field is missing. */}
      {testResult && (
        <div
          className={`rounded-lg border p-3 text-xs space-y-2 ${testResult.ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
          data-testid="quoter-selftest-result">
          <div className={`flex items-center gap-2 font-semibold ${testResult.ok ? "text-emerald-700" : "text-amber-800"}`}>
            {testResult.ok
              ? <><Check className="h-4 w-4" /> Quoter SSO is wired up and ready</>
              : <><AlertTriangle className="h-4 w-4" /> Quoter SSO can&apos;t mint a token yet — {testResult.error || "check the fields below"}</>}
          </div>
          {testResult.quoter_username && (
            <div className="text-slate-600" data-testid="quoter-selftest-username">
              Tested with <code>quoter_username=&ldquo;{testResult.quoter_username}&rdquo;</code>
              {testResult.ok && testResult.sso_url_prefix && (
                <> against <code>{testResult.sso_url_prefix}</code></>
              )}
              .
            </div>
          )}
          {(testResult.checks || []).length > 0 && (
            <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {testResult.checks.map((c, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  {c.ok
                    ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                    : <X className="h-3.5 w-3.5 text-red-500" />}
                  <span className={c.ok ? "text-slate-700" : "text-slate-500 line-through"}>{c.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Iter 103 — Password policy card (Settings → Security).
 *
 * Scoped-save pattern (matches Quoter SSO card): own local state,
 * dirty tracking, and a self-contained "Save policy" button. Persists
 * `password_min_length`, `password_require_upper/lower/digit/special`
 * to db.settings and is mirrored by /api/auth/password-policy for the
 * Profile strength meter.
 */
function PasswordPolicyCard({ s, onSaved, canManage }) {
  const initial = () => ({
    password_min_length:      Number(s?.password_min_length ?? 10),
    password_require_upper:   !!(s?.password_require_upper ?? true),
    password_require_lower:   !!(s?.password_require_lower ?? true),
    password_require_digit:   !!(s?.password_require_digit ?? true),
    password_require_special: !!(s?.password_require_special ?? false),
  });
  const [form, setForm] = useState(initial);
  const [baseline, setBaseline] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const next = initial();
    setForm(next);
    setBaseline(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.password_min_length, s?.password_require_upper, s?.password_require_lower,
      s?.password_require_digit, s?.password_require_special]);

  const dirty = Object.keys(form).some((k) => form[k] !== baseline[k]);
  const save = async () => {
    if (!dirty || saving || !canManage) return;
    const min = Math.max(6, Math.min(64, Number(form.password_min_length) || 10));
    setSaving(true);
    try {
      await api.put("/settings", {
        password_min_length:      min,
        password_require_upper:   !!form.password_require_upper,
        password_require_lower:   !!form.password_require_lower,
        password_require_digit:   !!form.password_require_digit,
        password_require_special: !!form.password_require_special,
      });
      toast.success("Password policy saved");
      if (typeof onSaved === "function") await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (key, label, hint) => (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
      <div>
        <div className="text-sm font-medium text-slate-700">{label}</div>
        {hint && <div className="text-xs text-slate-400">{hint}</div>}
      </div>
      <Switch
        checked={!!form[key]}
        onCheckedChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
        disabled={!canManage || saving}
        data-testid={`pw-policy-${key}-switch`}
      />
    </div>
  );

  return (
    <Card className="border-slate-200 shadow-sm p-6 space-y-4 lg:col-span-2" data-testid="settings-password-policy-card">
      <div>
        <h3 className="font-heading text-sm font-semibold text-slate-800">Password policy</h3>
        <p className="text-xs text-slate-400">
          Enforced server-side on every self-service password change (Profile page) and admin password reset. The Profile strength meter mirrors these rules exactly.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-slate-700">Minimum length</div>
            <div className="text-xs text-slate-400">Range 6–64 characters. Default 10.</div>
          </div>
          <Input
            type="number"
            min={6}
            max={64}
            value={form.password_min_length}
            onChange={(e) => setForm({ ...form, password_min_length: Number(e.target.value) || 0 })}
            disabled={!canManage || saving}
            className="w-20"
            data-testid="pw-policy-min-length-input"
          />
        </div>
        {toggleRow("password_require_upper",   "Require uppercase letter", "e.g. A–Z at least once")}
        {toggleRow("password_require_lower",   "Require lowercase letter", "e.g. a–z at least once")}
        {toggleRow("password_require_digit",   "Require digit",            "e.g. 0–9 at least once")}
        {toggleRow("password_require_special", "Require special character","e.g. !@#$ at least once")}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        {dirty && (
          <span className="text-[11px] italic text-amber-700" data-testid="pw-policy-dirty">Unsaved changes</span>
        )}
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving || !canManage}
          data-testid="pw-policy-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save policy
        </Button>
      </div>
    </Card>
  );
}

